import {Resource} from 'i18next';
import {ActivityEndType} from '../backend/enums/activity-end-type';
import {AffiliationType} from '../backend/enums/affiliation-type';
import {BackendLanguageTag} from '../backend/enums/backend-language-tag';
import {CameraFacing} from '../backend/enums/camera-facing';
import {CaregiverRelationshipRole} from '../backend/enums/caregiver-relationship-role';
import {DeviceType} from '../backend/enums/device-type';
import {EntitySpecialPurpose} from '../backend/enums/entity-special-purpose';
import {EntityType} from '../backend/enums/entity-type';
import {InviteState} from '../backend/enums/invite-state';
import {PlaylistType} from '../backend/enums/playlist-type';
import {MobileNetworkType} from '../backend/enums/mobile-network-type';
import {OperativeSystemName} from '../backend/enums/operative-system-name';
import {ProcessStatus} from '../backend/enums/process-status';
import {RegionCode} from '../backend/enums/region-code';
import {RelationshipType} from '../backend/enums/relationship-type';
import {ReminderResponseValue} from '../backend/enums/reminder-response-value';
import {UserGender} from '../backend/enums/user-gender';
import {UserSignInType} from '../backend/enums/user-sign-in-type';
import {UserType} from '../backend/enums/user-type';
import {InviteContactMethod} from '../enums/invite-contact-method';
import {SignInError} from '../enums/sign-in-error';
import {TimeLapseType} from '../enums/time-lapse-type';
import {Religion} from '../backend/enums';
import {CaregiverTaskCompletion} from '../enums/caregiver-task-completion';
import {CareTaskNotDoneReason} from '../backend/enums/care-task-not-done-reason';
import {CareTaskFeature} from '../backend/enums/care-task-feature';
import {RolePermissionType} from '../backend/enums/role-permission-type';
import {PosologySchedule} from '../backend/enums/posology-schedule';
import {Frequency} from '../types';
import {MaritalStatus} from '../backend/enums/marital-status';
import {EducationLevel} from '../backend/enums/education-level';

/*
 * Language Tags (for the App)
 */
export enum AppLanguageTag {
  /*
   * Portuguese from Portugal
   */
  ptPT = 'pt_PT',

  /*
   * Portuguese from Brazil
   */
  ptBR = 'pt_BR',

  /*
   * English from US
   */
  enUS = 'en_US',

  /*
   * Spanish from Spain
   */
  esES = 'es_ES',

  /*
   * Galician from Spain
   */
  glES = 'gl_ES',
}

export type LanguagesResource = Resource & {
  [language in AppLanguageTag]: LanguageResource;
};

export interface LanguageResource {
  translation: LanguageTranslation;
}

export interface LanguageTranslation {
  family_applications: {
    title: string;
    entity: string;
    choose_entity: string;
    no_affiliations: string;
    missing_affiliations: string;
    load_error: string;
    retry: string;
    more: string;
    back: string;
    access: string;
    enabled: string;
    disabled: string;
    enable: string;
    disable: string;
    confirm: string;
    write_error: string;
    write_success: string;
  };
  reminders: {
    group_time: string;
    group_reminder: string;
    group_repeats: string;
    group_recipients: string;
    group_actions: string;
    no_entity_assigned: string;
    group_switch_entity: string;
    group_edit: string;
    group_delete: string;
    group_delete_confirm: string;
    group_partial: string;
    group_future_required: string;
    group_advanced: string;
    my_title: string;
    my_scope: string;
    creation_scope: string;
    recipient: string;
    title: string;
    subtitle: string;
    add: string;
    bulk_title: string;
    bulk_success: string;
    bulk_partial: string;
    edit: string;
    loading: string;
    empty_title: string;
    empty_description: string;
    load_more: string;
    retry: string;
    load_error: string;
    senior_unavailable: string;
    delete_title: string;
    delete_description: string;
    delete: string;
    cancel: string;
    enable: string;
    disable: string;
    status_active: string;
    status_inactive: string;
    next_run: string;
    save_error: string;
    save_success: string;
    delete_error: string;
    delete_success: string;
    active_error: string;
    active_success: string;
    inactive_success: string;
    response_types: {
      yes_no: string;
      ok: string;
    };
    recurrence: {
      once: string;
      per_minute: string;
      hourly: string;
      daily: string;
      weekly: string;
      monthly: string;
      yearly: string;
      current_unsupported: string;
    };
    form: {
      title: string;
      title_placeholder: string;
      message: string;
      message_placeholder: string;
      response_type: string;
      date: string;
      time: string;
      recurrence: string;
      recurrence_count: string;
      save: string;
      title_required: string;
      title_too_long: string;
      message_required: string;
      message_too_long: string;
      date_required: string;
      time_required: string;
      invalid_date_time: string;
      date_in_past: string;
      time_in_past: string;
    };
  };
  family_services: {
    title: string;
    choose: string;
    none: string;
    unavailable: string;
    empty: string;
    error: string;
    denied: string;
    not_found: string;
    retry: string;
    call: string;
    no_phone: string;
    unsupported: string;
    active: string;
    inactive: string;
    enable: string;
    disable: string;
    confirm: string;
    cancel: string;
    save_error: string;
    saved: string;
    refresh_error: string;
  };
  family_invite: {
    contact_method: string;
    phone: string;
    action: string;
    context: string;
    name: string;
    email: string;
    next: string;
    back: string;
    send: string;
    confirm: string;
    success: string;
    uncertain: string;
    invalid: string;
    denied: string;
    unavailable: string;
  };
  family_profile: {
    user_details: string;
    remove_link: string;
    remove_link_success: string;
    remove_link_confirm: string;
    remove_link_error: string;
    remove_link_denied: string;
    cancel: string;
    institutions: string;
    senior_contacts: string;
    senior_empty: string;
    title: string;
    contacts: string;
    empty: string;
    load_more: string;
    loading: string;
    error: string;
    denied: string;
    unavailable: string;
    retry: string;
    close: string;
  };
  family_media: {
    recording: string;
    camera: string;
    take_photo: string;
    record_video: string;
    stop_recording: string;
    camera_error: string;
    title: string;
    download: string;
    share: string;
    export_error: string;
    upload: string;
    refresh: string;
    loading: string;
    empty_title: string;
    empty_description: string;
    load_error: string;
    refresh_error: string;
    load_more: string;
    loading_more: string;
    retry: string;
    senior_unavailable: string;
    photo: string;
    video: string;
    processing: string;
    processing_failed: string;
    uploaded_by: string;
    you: string;
    created_at: string;
    description: string;
    edit: string;
    delete: string;
    cancel: string;
    save: string;
    saving: string;
    upload_photo: string;
    upload_progress: string;
    invalid_file_type: string;
    upload_request_error: string;
    upload_transfer_error: string;
    upload_record_error: string;
    upload_success: string;
    edit_success: string;
    edit_error: string;
    router_prompt_edit_title: string;
    router_prompt_edit_message: string;
    delete_success: string;
    delete_error: string;
    delete_confirm_title: string;
    delete_confirm_description: string;
    detail_not_found: string;
    detail_load_error: string;
    detail_unavailable: string;
    signed_url_error: string;
  };
  legal_documents: {
    terms_and_conditions: string;
    privacy_policy: string;
  };
  products: {
    bluebird: string;
    bluebird_with_gender: string;
  };
  relationship_types: {
    [RelationshipType.Family]: string;
    [RelationshipType.Friend]: string;
    [RelationshipType.Healthcare]: string;
    [RelationshipType.Service]: string;
    [RelationshipType.Other]: string;
  };
  user_types: {
    [UserType.Caregiver]: string;
    [UserType.Follower]: string;
    [UserType.Senior]: string;
  };
  genders: {
    [UserGender.Female]: string;
    [UserGender.Male]: string;
    [UserGender.Undisclosed]: string;
    [UserGender.Unspecified]: string;
  };
  marital_statuses: {
    [MaritalStatus.CivilUnion]: string;
    [MaritalStatus.Divorced]: string;
    [MaritalStatus.Married]: string;
    [MaritalStatus.Other]: string;
    [MaritalStatus.Single]: string;
    [MaritalStatus.Widowed]: string;
  };
  education_levels: {
    [EducationLevel.BachelorsDegree]: string;
    [EducationLevel.BasicEducation]: string;
    [EducationLevel.DoctoratePhd]: string;
    [EducationLevel.MastersDegree]: string;
    [EducationLevel.NoFormalEducation]: string;
    [EducationLevel.Other]: string;
    [EducationLevel.PostgraduateDiploma]: string;
    [EducationLevel.PrimaryEducation]: string;
    [EducationLevel.SecondaryEducation]: string;
    [EducationLevel.VocationalTechnicalTraining]: string;
  };
  languages: {
    [BackendLanguageTag.ptPT]: string;
    [BackendLanguageTag.enUS]: string;
    [BackendLanguageTag.esES]: string;
    [BackendLanguageTag.glES]: string;
    [BackendLanguageTag.ptBR]: string;
  };
  regions: {
    [RegionCode.PT]: string;
    [RegionCode.ES]: string;
    [RegionCode.GL]: string;
    [RegionCode.BR]: string;
  };
  roles: {
    [CaregiverRelationshipRole.Administrator]: string;
    [CaregiverRelationshipRole.Contact]: string;
    [CaregiverRelationshipRole.Member]: string;
  };
  religions: {
    [Religion.AfricanTraditionalAndDiasporic]: string;
    [Religion.Agnostic]: string;
    [Religion.Animism]: string;
    [Religion.Atheist]: string;
    [Religion.BahaI]: string;
    [Religion.Buddhism]: string;
    [Religion.CaoDai]: string;
    [Religion.ChineseTraditionalReligion]: string;
    [Religion.Christianity]: string;
    [Religion.Hinduism]: string;
    [Religion.Islam]: string;
    [Religion.Jainism]: string;
    [Religion.Juche]: string;
    [Religion.Judaism]: string;
    [Religion.NeoPaganism]: string;
    [Religion.Nonreligious]: string;
    [Religion.Rastafari]: string;
    [Religion.Secular]: string;
    [Religion.Shinto]: string;
    [Religion.Sikhism]: string;
    [Religion.Spiritism]: string;
    [Religion.Tenrikyo]: string;
    [Religion.UnitarianUniversalism]: string;
    [Religion.Zoroastrianism]: string;
    [Religion.PrimalIndigenous]: string;
    [Religion.Undisclosed]: string;
    [Religion.Unspecified]: string;
  };
  affiliation_types: {
    [AffiliationType.Senior]: string;
    [AffiliationType.Caregiver]: string;
    [AffiliationType.Administrator]: string;
    [AffiliationType.Manager]: string;
    [AffiliationType.Operator]: string;
    [AffiliationType.MedicalProfessional]: string;
  };
  role_permissions_section: {
    overview: string;
    seniors: string;
    team: string;
    'digital-inclusion': string;
    settings: string;
    'daily-care-log': string;
    calendar: string;
    occurrences: string;
    'quick-access-devices': string;
  };
  role_permissions: {
    [RolePermissionType.Overview_Activity]: string;
    [RolePermissionType.Overview_Statistics]: string;
    [RolePermissionType.Seniors_List]: string;
    [RolePermissionType.Seniors_Dashboard]: string;
    [RolePermissionType.Seniors_Dashboard_Activity]: string;
    [RolePermissionType.Seniors_Dashboard_GeneralStatistics]: string;
    [RolePermissionType.Seniors_Dashboard_HealthStatistics]: string;
    [RolePermissionType.Seniors_IndividualPlan]: string;
    [RolePermissionType.Seniors_PersonalData_Reminders]: string;
    [RolePermissionType.Seniors_BADL]: string;
    [RolePermissionType.Seniors_IADL]: string;
    [RolePermissionType.Seniors_ClinicalSupport]: string;
    [RolePermissionType.Seniors_PersonalDevelopment]: string;
    [RolePermissionType.Seniors_CareAndActivities]: string;
    [RolePermissionType.Seniors_CareAndActivities_BADL]: string;
    [RolePermissionType.Seniors_CareAndActivities_IADL]: string;
    [RolePermissionType.Seniors_CareAndActivities_ClinicalSupport]: string;
    [RolePermissionType.Seniors_CareAndActivities_PersonalDevelopment]: string;
    [RolePermissionType.Seniors_CareServices]: string;
    [RolePermissionType.Seniors_PersonalData_Profile]: string;
    [RolePermissionType.Seniors_PersonalData_Caregivers]: string;
    [RolePermissionType.Seniors_PersonalData_Health]: string;
    [RolePermissionType.Seniors_DigitalInclusion_Applications]: string;
    [RolePermissionType.Seniors_DigitalInclusion_Devices]: string;
    [RolePermissionType.Seniors_DigitalInclusion_Playlists]: string;
    [RolePermissionType.Seniors_DigitalInclusion_Cards]: string;
    [RolePermissionType.Seniors_PersonalData_Attachments]: string;
    [RolePermissionType.Team_Professionals]: string;
    [RolePermissionType.Team_CareServices]: string;
    [RolePermissionType.Team_Professionals_Profile]: string;
    [RolePermissionType.Team_Professionals_Applications]: string;
    [RolePermissionType.Team_Professionals_Cards]: string;
    [RolePermissionType.Team_Professionals_Playlists]: string;
    [RolePermissionType.Team_Professionals_CareServices]: string;
    [RolePermissionType.Team_Shifts_Management]: string;
    [RolePermissionType.Team_Shifts_Attributions]: string;
    [RolePermissionType.Team_Permissions]: string;
    [RolePermissionType.Team_AttendanceTracker_Registry]: string;
    [RolePermissionType.Team_AttendanceTracker_Assignments]: string;
    [RolePermissionType.Settings_Entity_Profile]: string;
    [RolePermissionType.Settings_Entity_Settings]: string;
    [RolePermissionType.Settings_Licenses]: string;
    [RolePermissionType.Settings_BADL]: string;
    [RolePermissionType.Settings_IADL]: string;
    [RolePermissionType.Settings_ClinicalSupport]: string;
    [RolePermissionType.Settings_PersonalDevelopment]: string;
    [RolePermissionType.Settings_Templates]: string;
    [RolePermissionType.Settings_Occurrences]: string;
    [RolePermissionType.DailyCareLog]: string;
    [RolePermissionType.DailyCareLog_BADL]: string;
    [RolePermissionType.DailyCareLog_IADL]: string;
    [RolePermissionType.DailyCareLog_PersonalDevelopment]: string;
    [RolePermissionType.DailyCareLog_ClinicalSupport]: string;
    [RolePermissionType.DailyCareLog_Occurrences]: string;
    [RolePermissionType.DigitalInclusion_Applications]: string;
    [RolePermissionType.DigitalInclusion_Media]: string;
    [RolePermissionType.DigitalInclusion_Devices]: string;
    [RolePermissionType.DigitalInclusion_Cards]: string;
    [RolePermissionType.DigitalInclusion_Playlists]: string;
    [RolePermissionType.DigitalInclusion_Services]: string;
    [RolePermissionType.Calendar]: string;
    [RolePermissionType.Calendar_BADL]: string;
    [RolePermissionType.Calendar_IADL]: string;
    [RolePermissionType.Calendar_PersonalDevelopment]: string;
    [RolePermissionType.Calendar_ClinicalSupport]: string;
    [RolePermissionType.Occurrences]: string;
    [RolePermissionType.SettingsQuickAccessDevices]: string;
  };
  screen_paths: {
    professional_caregiver_seniors: string;
    professional_caregiver_team: string;
    professional_caregiver_roles_and_permissions: string;
    senior_dashboard: string;
    senior_individual_plan: string;
    senior_care_plan: string;
    senior_care_and_activity: string;
    senior_personal_data: string;
    senior_profile: string;
    senior_caregivers: string;
    senior_applications: string;
    senior_devices: string;
    senior_activity: string;
    senior_user_cards: string;
    senior_statistics: string;
    senior_health: string;
    senior_playlists: string;
    senior_attachments: string;
    institution_settings_profile: string;
    institution_settings_settings: string;
    institution_settings_activity: string;
    institution_settings_user_cards: string;
    institution_settings_devices: string;
    institution_settings_services: string;
    institution_settings_playlist: string;
    institution_settings_statistics: string;
    institution_settings_licenses: string;
    institution_settings_templates: string;
    institution_settings_occurrences: string;
    institution_settings_quick_access_devices: string;
  };
  institution_settings_screen: {
    subtitle: {
      write: string;
      read: string;
    };
    columns: {
      setting: string;
      active: string;
    };
    attendance_tracker: string;
    load_error_toast: string;
    update_error_toast: string;
  };
  attendance_tracker_registry_screen: {
    subtitle: string;
    filters: {
      all_statuses: string;
      all_day: string;
      all_professionals: string;
    };
    empty: string;
    load_error: string;
    retry: string;
    waiting_exit: string;
    add: {
      title: string;
      professional: string;
      professionals_hint: string;
      professionals_empty: string;
      professional_placeholder: string;
      kind_label: string;
      kind_session: string;
      kind_entry: string;
      kind_exit: string;
      open_session_info: string;
      entry_date: string;
      entry_time: string;
      exit_date: string;
      exit_time: string;
      reason: string;
      reason_placeholder: string;
      notice: string;
      self_warning: string;
      self_action: string;
      cancel: string;
      continue: string;
      back: string;
      confirm: string;
      confirm_title: string;
      confirm_description: string;
      summary_record: string;
      success: string;
      checking: string;
      error_future: string;
      error_order: string;
      error_overlap: string;
      error_type: string;
      error_conflict: string;
      error_not_tracking: string;
      error_generic: string;
      validation_future: string;
      validation_exit_before_entry: string;
      validation_exit_before_open_entry: string;
      validation_reason_required: string;
    };
    void: {
      title: string;
      description: string;
      reason: string;
      reason_placeholder: string;
      cancel: string;
      confirm: string;
      success: string;
      error_conflict: string;
      error_self: string;
      error_generic: string;
      validation_reason_required: string;
    };
    correct: {
      title: string;
      description: string;
      current_session: string;
      reason: string;
      notice: string;
      confirm_title: string;
      confirm_description: string;
      summary_old: string;
      summary_new: string;
      confirm: string;
      success: string;
    };
    details: {
      correct_action: string;
      void_action: string;
      duration: string;
      title: string;
      close: string;
    };
  };
  attendance_tracker_assignments_screen: {
    subtitle: {
      write: string;
      read: string;
    };
    columns: {
      professional: string;
      assignment: string;
      last_change: string;
    };
    assignment_types: {
      mandatory: string;
      optional: string;
      not_applicable: string;
    };
    empty: string;
    last_change_summary: string;
    last_change_summary_no_author: string;
    load_error_toast: string;
    update_error_toast: string;
  };
  date_time_picker: {
    cancel_button: string;
    confirm_button: string;
    clear_button: string;
    today: string;
  };
  date_range_input: {
    title: string;
    presets: {
      day: string;
      week: string;
      month: string;
      '7_days': string;
      '15_days': string;
      '30_days': string;
      '180_days': string;
      '365_days': string;
      custom: string;
    };
    ok_button: string;
    cancel_button: string;
    errors: {
      invalid_date_range: string;
    };
  };
  generic_header: {
    actions: {
      edit: string;
      delete: string;
      save: string;
      import: string;
      archivePlan: string;
      remove: string;
    };
  };
  user_card: {
    actions: {
      edit: string;
      delete: string;
    };
  };
  role_autocomplete: {
    unselected: string;
    no_matching: string;
  };
  view_type_toggler: {
    tooltip_message: string;
  };
  index_grid_screen: {
    total_counter: string;
    add_button: string;
    search_placeholder: string;
    filter_placeholder: string;
    empty_message_add_encouraging: string;
    empty_search_message: {
      title: string;
      subtitle: string;
    };
    empty_filters_message: {
      title: string;
      subtitle: string;
    };
    toggler: {
      list: string;
      grid: string;
    };
  };
  login: {
    welcome: string;
    welcome_daily_care_log: string;
    welcome_accept_invite: string;
    instruction: string;
    email_input: string;
    password_input: string;
    enter_button: string;
    enter_button_microsoft: string;
    enter_button_google: string;
    forgot_password: string;
    quick_access: string;
    validate_invite: string;
    use_invite_code: string;
    errors: {
      form_validation_invalid_field: string;
      [SignInError.Unknown]: string;
      [SignInError.InvalidEmail]: string;
      [SignInError.UserDisabled]: string;
      [SignInError.UserNotFound]: string;
      [SignInError.WrongPassword]: string;
      [SignInError.FailedToGetMe]: string;
    };
  };
  recover_password: {
    title: string;
    subtitle: string;
    instructions: string;
    email_input: string;
    send_button: string;
    password_remembered: string;
    go_back_button: string;
    errors: {
      form_validation_invalid_field: string;
      send_request_error_message: string;
    };
    success: {
      send_request_success_message: string;
    };
  };
  replace_password: {
    title: string;
    instructions: string;
    new_password_input: string;
    repeat_password_input: string;
    submit_button: string;
    password_remembered: string;
    go_back_button: string;
    errors: {
      form_validation_invalid_field: string;
      form_validation_invalid_password_minimum_characters: string;
      form_validation_invalid_password_blank_spaces: string;
      form_validation_invalid_repeat_password_mismatch: string;
      change_password_error_message: string;
    };
    success: {
      change_password_success_message: string;
    };
  };
  validate_invite: {
    title: string;
    instructions_1: string;
    instructions_2: string;
    code_input: string;
    send_button: string;
    errors: {
      form_validation_invalid_field: string;
      invalid_invite_error_message: string;
    };
  };
  accept_invite: {
    relationship_invite_title: string;
    relationship_invite_instructions: string;
    affiliation_invite_title: string;
    affiliation_invite_instructions: string;
    affiliation_type_description: string;
    create_account_button: string;
    login_button: string;
    errors: {
      invalid_invite_error_message: string;
    };
  };
  quick_access_login: {
    instruction: string;
    all_users: string;
    error: string;
    back_to_email_and_password_login: string;
    go_back_to_the_recent_users_list: string;
    hello_user: string;
    insert_your_quick_access_code: string;
    login_failed: string;
    login_failed_code: string;
    try_again: string;
    regain_access: string;
    remove_recent: string;
    regain_access_message: string;
    regain_access_description: string;
    back_to_previous: string;
  };
  notifications: {
    date: string;
    time: string;
    title: string;
    check_read: string;
    check_unread: string;
    read_all_notifications: string;
    read_all_notifications_dialog_title: string;
    read_all_notifications_dialog_content: string;
    read_all_notifications_disabled_tooltip_message: string;
    read_all_notifications_success_message: string;
    read_all_notifications_error_message: string;
  };
  push_notifications: {
    warnings: {
      unsubscribed_generic_warning_title: string;
      unsubscribed_not_supported_warning_message: string;
      unsubscribed_error_warning_message: string;
      unsubscribed_permissions_denied_warning_message: string;
    };
  };
  invite_modal: {
    caregiver_invite_title: string;
    affiliation_type_description: string;
    relationship_type_input: string;
    invite_recipient_not_current_user_description: string;
    accept_button: string;
    decline_button: string;
    relationship_already_exists_description: string;
    affiliation_already_exists_description: string;
    invite_done_unknown_description: string;
    invite_done_refused_description: string;
    invite_done_accepted_description: string;
    invite_done_cancelled_description: string;
    invite_done_expired_description: string;
    loading_invite_description_1: string;
    loading_invite_description_2: string;
    errors: {
      refuse_invite_error_message: string;
      accept_invite_error_message: string;
      fetch_invite_error_message: string;
      fetch_invite_invalid_error_message: string;
    };
    success: {
      refuse_relationship_invite_success_message: string;
      accept_relationship_invite_success_message: string;
      refuse_affiliation_invite_success_message: string;
      accept_affiliation_invite_success_message: string;
    };
  };
  profile: {
    title: string;
    exit_button: string;
    personal_information_section_title: string;
    change_theme_section_title: string;
    legal_information_section_title: string;
    relationships_section_title: string;
    affiliations_section_title: string;
    logout_normal_button: string;
    logout_loading_button: string;
    logout_error_button: string;
    theme_input: string;
    dark_option: string;
    light_option: string;
    system_option: string;
  };
  create_caregiver_account: {
    register_title: string;
    essential_information_step_description: string;
    essential_information_step: string;
    personal_information_step: string;
    optional_information_step: string;
    note_title: string;
    note_statement: string;
    continue_button: string;
    create_account_button: string;
    start_using_button: string;
    go_back_to_login_button: string;
    go_back_button: string;
    success_title: string;
    success_description: string;
    error_title: string;
    error_description: string;
    relationship_type_input: string;
    profession_input: string;
    profession_description_input: string;
    email_address_input: string;
    password_input: string;
    repeat_password_input: string;
    legal_documents_title: string;
    legal_documents_statement: string;
    full_name_input: string;
    display_name_input: string;
    mobile_number_input: string;
    profile_picture_input: string;
    gender_input: string;
    gender_placeholder_input: string;
    language_input: string;
    language_placeholder_input: string;
    birthday_input: string;
    birthday_input_placeholder: string;
    tax_identification_number_input: string;
    profile_picture_info_message: string;
    display_name_info_message: string;
    errors: {
      form_validation_invalid_field: string;
      form_validation_invalid_email: string;
      form_validation_invalid_password_minimum_characters: string;
      form_validation_invalid_password_blank_spaces: string;
      form_validation_invalid_repeat_password_mismatch: string;
      form_validation_invalid_full_name_maximum_characters: string;
      form_validation_invalid_display_name_maximum_characters: string;
      form_validation_invalid_mobile_phone_number: string;
      form_validation_not_mobile_phone_number: string;
      form_validation_invalid_tax_identification_number: string;
      form_validation_invalid_legal_documents: string;
      enter_account_error_message: string;
      invalid_invite_error_message: string;
    };
  };
  office: {
    generics: {
      cancel: string;
      save: string;
      exit: string;
      back: string;
      apply: string;
      save_pdf: string;
      save_xlsx: string;
    };
    localized_string_input: {
      fields: {
        placeholder: string;
        rules: {
          required: string;
          max_length: string;
        };
      };
    };
    users_filters: {
      title: string;
      clear_all: string;
      sections: {
        socialResponses: {title: string};
        shifts: {title: string};
        activeStatus: {
          title: string;
          options: {
            active: string;
            inactive: string;
          };
        };
        taskStatus: {
          title: string;
          options: {
            planned: string;
            handled: string;
            done: string;
            not_done: string;
            not_handled: string;
          };
        };
        taskGroups: {
          title: string;
        };
        patientOccurrences: {
          title: string;
        };
        generalOccurrences: {
          title: string;
        };
      };
    };
    profile_type_tabs: {
      informal_caregiver: string;
      professional_caregiver: string;
      system_administrator: string;
      partner: string;
    };
    informal_caregiver: {
      tabs: {
        activity: string;
        profile: string;
        media: string;
        reminders: string;
      };
      add_menu: {
        button: string;
        title: string;
        create_reminder: string;
        send_photo: string;
        send_video: string;
        send_invite: string;
      };
      relationship_picker: {
        caption: string;
        dialog_title: string;
      };
      caregiver_app_info_message: string;
    };
    professional_caregiver: {
      tabs: {
        statistics: string;
        digital_inclusion: string;
        seniors: string;
        applications: string;
        services: string;
        professional_caregivers: string;
        activity: string;
        devices: string;
        user_cards: string;
        licenses: string;
        entity_photos: string;
        playlists: string;
        start: string;
        planner: string;
        occurrences: string;
        settings: string;
        sioslifeAcademy: string;
        myToDos: string;
      };
      profile: {
        generic_header: {
          title: string;
          subtitle: {
            write: string;
            read: string;
          };
          breadcrumbs: {
            team: string;
            professionals: string;
            profile: string;
          };
        };
      };
      affiliation_picker: {
        caption: string;
        dialog_title: string;
      };
      settings: {
        generic_header: {
          breadcrumbs: {
            settings: string;
            institution: string;
            licenses: string;
            templates: string;
            occurrences: string;
            quick_access_devices: string;
          };
          subtitle: {
            institution: {
              write: string;
              read: string;
            };
            activities_sector: string;
            licenses: string;
            templates: {
              write: string;
              read: string;
            };
            occurrences: string;
            quick_access_devices: string;
          };
        };
        templates: {
          empty: string;
          name: string;
          area: string;
          description: string;
          update: string;
          edit: string;
          delete: string;
          revert: string;
          duplicate: string;
          cancel: string;
          delete_title: string;
          delete_subtitle: string;
          snackbar: {
            delete_success_message: string;
            delete_error_message: string;
            duplicate_success_message: string;
            duplicate_error_message: string;
            edit_name_success_message: string;
            edit_name_error_message: string;
          };
          edit_name: string;
          alert: string;
          no_permissions: string;
        };
        occurrences: {
          patients: string;
          general: string;
          general_plural: string;
          occurrence: string;
          active: string;
          action_required: string;
          notify: string;
          all_profiles: string;
          add: string;
          add_modal_title: string;
          add_modal_name: string;
          add_name_error: string;
          delete_modal_title: string;
          revert_modal_title: string;
          revert_modal_subtitle: string;
          delete_modal_subtitle: string;
          name_error: string;
          add_error_toast: string;
          add_success_toast: string;
          edit_error_toast: string;
          edit_success_toast: string;
          delete_error_toast: string;
          delete_success_toast: string;
          revert_success_toast: string;
          delete_default: string;
          edit_name_default: string;
          default: string;
          empty_general: string;
          empty_patients: string;
          no_permissions: string;
        };
        quick_access: {
          empty: string;
          name: string;
          type: string;
          access: string;
          professional: string;
          deactivate: string;
          activate_device: string;
          activate_disabled: string;
          activate_disabled_ist: string;
          no_permissions: string;
          activate_modal: {
            title: string;
            device_name: string;
            device_type: string;
            success_message: string;
            error_message: string;
            name_repeat_error: string;
          };
          edit_modal: {
            title: string;
            success_message: string;
            error_message: string;
          };
          deactivate_modal: {
            title: string;
            subtitle: string;
            success_message: string;
            error_message: string;
            button: string;
            no_name: string;
          };
        };
      };
      seniors: {
        edit: {
          profile: {
            generic_header: {
              subtitle: {
                write: string;
              };
            };
          };
        };
        table_view_screen: {
          name: string;
          social_response: string;
          age: string;
          room: string;
          status: string;
          role: string;
          phone: string;
          email: string;
        };
      };
      shifts_allocations: {
        generic_header: {
          breadcrumbs: {
            team: string;
            shifts_allocations: string;
          };
        };
        exit_edit_mode: string;
        table: {
          collaborators: string;
          today: string;
          select_shift: string;
        };
      };
      roles_and_permissions: {
        generic_header: {
          breadcrumbs: {
            team: string;
            roles_and_permissions: string;
          };
          subtitle: string;
        };
        create: string;
        edit: string;
        delete: string;
        default_role: string;
        confirm_delete: {
          title: string;
          copy: {
            title: string;
            subtitle: string;
          };
          snackbar: {
            error_message: string;
          };
        };
      };
      occurrences: {
        empty_title: string;
        empty_subtitle: string;
        table_header_date: string;
        table_header_time: string;
        table_header_type: string;
        table_header_patient: string;
        table_header_responsible: string;
        table_header_registered: string;
        table_header_status: string;
        table_header_knowledge: string;
        add_occurrence: string;
        type_placeholder: string;
        local_placeholder: string;
        patient_placeholder: string;
        responsible_placeholder: string;
        details_placeholder: string;
        status: {
          registered: string;
          not_handled: string;
          handled: string;
        };
        details: string;
        information: string;
        date_hour: string;
        acknowledge: string;
        add_notes: string;
        write_notes: string;
        acknowledge_success: string;
        acknowledge_error: string;
        handle_success: string;
        handle_error: string;
        responsible: string;
        registered_by: string;
        took_acknowledge: string;
        history: {
          acknowledge: string;
          state_update: string;
        };
        notes_success: string;
        notes_error: string;
      };
    };
    system_administrator: {
      tabs: {
        statistics: string;
        activity: string;
        devices: string;
        apiDocs: string;
        applications: string;
        entities: string;
        cards: string;
        licenses: string;
        seniors: string;
        caregivers: string;
        playlists: string;
        gdprErasureRequests: string;
      };
    };
    partner: {
      tabs: {
        home: string;
        apiDocs: string;
      };
    };
    legacy_integration: {
      entity_services_disabled_alert_title: string;
      entity_services_disabled_alert_message: string;
    };
    none: {
      tabs: {
        home: string;
      };
    };
    permissions: {
      access_denied_message: string;
    };
    access: {
      denied_message_first: string;
      denied_message_second: string;
      care_management: string;
      digital_inclusion: string;
      speak: string;
      tooltip: string;
    };
    pending_tasks: {
      alert: string;
      see_tasks: string;
      header_title: string;
      info: string;
      empty: string;
      all: string;
      validate: string;
      modal_title: string;
      modal_observation: string;
      modal_validate: string;
      snackbar_success: string;
      snackbar_error: string;
      modal_task_validation_title: string;
    };
  };
  seniors: {
    root: {
      title: string;
      add_button: string;
      search_input_placeholder: string;
      name_header: string;
      gender_header: string;
      state_header: string;
      state_value_true: string;
      state_value_false: string;
      no_seniors: string;
      deleted_senior_dialog_title: string;
      deleted_senior_dialog_message: string;
      deleted_senior_dialog_ok_button: string;
      senior: string;
      seniors: string;
      modals: {
        add_senior_flow: {
          form: {
            title: string;
            cancel: string;
            add: string;
            inputs: {
              active: string;
              name: {
                label: string;
                placeholder: string;
                required_message: string;
                pattern_message: string;
                max_message: string;
              };
              name_alias: {
                label: string;
                placeholder: string;
                required_message: string;
                pattern_message: string;
                max_message: string;
              };
              birthdate: {
                label: string;
                placeholder: string;
                required_message: string;
              };
            };
          };
          confirm: {
            title: string;
            confirm_title: string;
            confirm_subtitle: string;
            cancel: string;
            add: string;
          };
          snackbar: {
            success_message: string;
            error_message: string;
          };
        };
      };
    };
    edit: {
      senior_dcm: {
        careplan: {
          load_error: string;
          caregiver: {
            title: string;
            unnasigned: string;
            unnasigned_tooltip: string;
          };
          activities: {
            add_button: string;
            load_error: string;
            empty_state: {
              title: string;
              subtitle: string;
            };
          };
          on_save: {
            snackbar: {
              success_message: string;
              error_message: string;
            };
          };
          on_update: {
            snackbar: {
              success_message: string;
              error_message: string;
            };
          };
          on_delete: {
            title: string;
            description: string;
            snackbar: {
              success_message: string;
              error_message: string;
            };
          };
        };
        individual_plan: {
          add_cares: string;
          caregiver: {
            title: string;
            coordinator: string;
            unassigned: string;
            unassigned_tooltip: string;
          };
          empty_state: {
            title: string;
            subtitle: string;
          };
          observations: string;
          created_at: string;
          valid_until: string;
          not_defined: string;
          new_diagnostic: string;
          diagnostic: string;
          goal: string;
          new_goal: string;
          new_objective: string;
          history: {
            action: string;
            title: string;
            close: string;
            version: string;
            unknown_author: string;
            load_error: string;
            source: {
              manual: string;
              ai: string;
              restore: string;
            };
            summary: {
              entity: {
                plan: string;
                diagnostic: string;
                goal: string;
                objective: string;
              };
              entity_plural: {
                plan: string;
                diagnostic: string;
                goal: string;
                objective: string;
              };
              field: {
                definition: string;
                responsibleUserId: string;
                cares: string;
                activityTypesIds: string;
                indicators: string;
                isActive: string;
                coordinatorUserId: string;
                validUntil: string;
                observations: string;
                diagnosticsOrder: string;
              };
              created: string;
              created_unnamed: string;
              created_many: string;
              deleted: string;
              deleted_unnamed: string;
              deleted_many: string;
              updated: string;
              updated_many: string;
              more: string;
              restored_from: string;
            };
            restore: {
              button: string;
              prompt_title: string;
              prompt_description: string;
              confirm: string;
              success: string;
              conflict: string;
              error: string;
            };
            view_version: string;
            back: string;
            detail_load_error: string;
            no_diagnostics: string;
            detail_note: string;
            change_type_unknown: string;
            change_type: {
              baseline: string;
              'plan-created': string;
              'plan-updated': string;
              'plan-archived': string;
              'plan-reactivated': string;
              'diagnostic-created': string;
              'diagnostic-updated': string;
              'diagnostic-deleted': string;
              'goal-created': string;
              'goal-updated': string;
              'goal-deleted': string;
              'objective-created': string;
              'objective-updated': string;
              'objective-deleted': string;
              'bulk-append': string;
              'bulk-replace': string;
              restored: string;
            };
          };
          ai_generate: {
            title: string;
            description: string;
            focus_label: string;
            focus_placeholder: string;
            automatic: string;
            with_focus: string;
          };
          ai_proposal: {
            title: string;
            subtitle: string;
            discard: string;
            approve: string;
            approved: string;
            replaced: string;
            apply_prompt: {
              title: string;
              description: string;
              replace: string;
              append: string;
            };
            rate_limited: string;
            save_error: string;
            invalid_references: string;
            error: string;
          };
          on_delete: {
            title: string;
            description: string;
            snackbar: {
              success_message: string;
              error_message: string;
            };
          };
          add_diagnostic: {
            title: string;
            edit: string;
            form: {
              definition: {
                label: string;
                placeholder: string;
                error_message: string;
              };
            };
          };
          on_diagnostic_remove: {
            header: string;
            title: string;
            description: string;
          };
          add_diagnostic_goal: {
            title: string;
            edit: string;
            form: {
              definition: {
                label: string;
                placeholder: string;
                error_message: string;
              };
              responsibleUser: {
                label: string;
                placeholder: string;
              };
            };
          };
          on_diagnostic_goal_remove: {
            header: string;
            title: string;
            description: string;
          };
          add_diagnostic_goal_objective: {
            title: string;
            edit: string;
            form: {
              definition: {
                label: string;
                placeholder: string;
                error_message: string;
              };
            };
            cares: {
              add_non_automatic: string;
              non_automatic_title: string;
              max_reached: string;
              too_long: string;
            };
            snackbar: {
              error: string;
              invalid_references: string;
            };
            table: {
              definition: string;
              cares: string;
              indicators: string;
              targets: string;
              execution: string;
            };
          };
          on_diagnostic_goal_objective_remove: {
            header: string;
            title: string;
            description: string;
          };
        };
      };
    };
  };
  senior_tabs: {
    tabs: {
      profile: string;
      caregivers: string;
      applications: string;
      devices: string;
      activity: string;
      statistics: string;
      user_cards: string;
      health: string;
      affiliations: string;
      playlists: string;
      external_accounts: string;
    };
    errors: {
      senior_does_not_exist_error: string;
    };
  };
  health_profile_form: {
    clinical_conditions_title: string;
    visual_difficulty_label: string;
    hearing_difficulty_label: string;
    cognitive_deficit_label: string;
    parkinson_label: string;
    alzheimer_label: string;
    other_dementias_label: string;
    reduced_mobility_upper_limbs_label: string;
    reduced_mobility_lower_limbs_label: string;
    paralysis_left_limb_label: string;
    paralysis_right_limb_label: string;
    wheelchair_label: string;
    illiterate_label: string;
    disable_clinical_conditions_tooltip: string;
    errors: {
      update_health_profile_error_message: string;
    };
    noHealthMeasurements: string;
  };
  health_charts: {
    noData: string;
    heartRate: {
      subtitle1: string;
    };
    bloodOxygen: {
      subtitle1: string;
      subtitle2: string;
    };
    bloodPressure: {
      subtitle1: string;
      subtitle2: string;
    };
    bloodGlucose: {
      subtitle1: string;
      subtitle2: string;
    };
    bodyTemperature: {
      subtitle1: string;
    };
    bodyWeight: {
      subtitle1?: string;
    };
    errors: {
      fetch: string;
    };
  };
  health_measurements: {
    title: string;
    units: {
      beatsPerMinute: string;
      breathsPerMinute: string;
    };
  };
  senior_caregivers_listing: {
    invite_caregiver_button: string;
    invites_history_button: string;
    no_caregivers: string;
    name_header: string;
    relationship_type_header: string;
    remove_relationship_header: string;
    remove_relationship_dialog_title: string;
    remove_relationship_dialog_message: string;
    remove_relationship_button: string;
    remove_relationship_button_tooltip: string;
    remove_relationship_error_message: string;
    remove_relationship_success_message: string;
  };
  senior_relationships_invites_history: {
    title: string;
    no_invites: string;
    invite_caregiver_button: string;
    cancel_button: string;
    process_closed: string;
    name_header: string;
    contact_header: string;
    state_header: string;
    status_header: string;
    cancel_invite_dialog_title: string;
    cancel_invite_dialog_message: string;
    cancel_invite_dialog_action_yes_button: string;
    cancel_invite_dialog_action_no_button: string;
    cancel_invite_error_message: string;
    cancel_invite_success_message: string;
  };
  informal_caregiver_invite_form: {
    title: string;
    name_input: string;
    contact_method_input: string;
    email_input: string;
    mobile_phone_number_input: string;
    save_button: string;
    saving_button: string;
    save_dialog_title: string;
    save_dialog_message: string;
    save_dialog_action_save_button: string;
    router_prompt_title: string;
    router_prompt_message: string;
    cancel_toast: string;
    success_message: string;
    errors: {
      form_validation_invalid_field: string;
      form_validation_invalid_mobile_phone_number: string;
      form_validation_not_mobile_phone_number: string;
      form_validation_invalid_email: string;
      error_message: string;
    };
  };
  devices_associations: {
    identifier_header: string;
    type_header: string;
    state_header: string;
    state_value_true: string;
    state_value_false: string;
    remove_device_header: string;
    no_devices: string;
    associate_device_button: string;
    remove_device_dialog_title: string;
    remove_device_dialog_message: string;
    remove_device_button: string;
    remove_device_button_disabled_tooltip: string;
    remove_device_error_message: string;
    remove_device_success_message: string;
  };
  user_cards_associations: {
    name_header: string;
    state_header: string;
    state_value_true: string;
    state_value_false: string;
    remove_card_header: string;
    associate_card_button: string;
    no_cards: string;
    remove_card_dialog_title: string;
    remove_card_dialog_message: string;
    remove_card_button: string;
    remove_card_button_disabled_tooltip: string;
    remove_card_error_message: string;
    remove_card_success_message: string;
  };
  associate_user_card_form: {
    title: string;
    card_name_input: string;
    entity_input: string;
    card_name_info_message: string;
    save_button: string;
    saving_button: string;
    save_dialog_title: string;
    save_dialog_message: string;
    save_dialog_action_cancel_button: string;
    save_dialog_action_save_button: string;
    router_prompt_title: string;
    router_prompt_message: string;
    router_prompt_action_cancel_button: string;
    router_prompt_action_ok_button: string;
    cancel_toast: string;
    no_affiliations_info_message: string;
    success: {save_success_button: string; success_message: string};
    errors: {
      error_message: string;
      error_on_save_button: string;
      form_validation_invalid_field: string;
    };
  };
  associate_device_form: {
    title: string;
    identifier_input: string;
    entity_input: string;
    save_button: string;
    saving_button: string;
    save_dialog_title: string;
    save_dialog_message: string;
    save_dialog_action_cancel_button: string;
    save_dialog_action_save_button: string;
    router_prompt_title: string;
    router_prompt_message: string;
    cancel_toast: string;
    no_affiliations_info_message: string;
    success: {save_success_button: string; success_message: string};
    errors: {
      error_on_save_button: string;
      form_validation_invalid_field: string;
      error_message: string;
    };
  };
  external_accounts: {
    types: {
      call: string;
      partner_p3_n1: string;
      partner_p3_n2: string;
      partner_p3_n3: string;
      partner_p3_n4: string;
    };
    list: {
      type_header: string;
      delete_account_header: string;
      delete_account_button: string;
      associate_account_button: string;
      no_accounts: string;
      error_message: string;
      delete_account_dialog_title: string;
      delete_account_dialog_description: string;
      delete_account_dialog_cannot_be_undone: string;
      delete_account_dialog_confirm_button: string;
      delete_account_dialog_cancel_button: string;
      delete_account_success_message: string;
      delete_account_error_message: string;
    };
    associate: {
      title: string;
      type_input: string;
      save_button: string;
      saving_button: string;
      save_dialog_title: string;
      save_dialog_message: string;
      save_dialog_action_cancel_button: string;
      save_dialog_action_save_button: string;
      no_available_types_message: string;
      success_message: string;
      errors: {
        generic_message: string;
        conflict_message: string;
        provider_error_message: string;
        error_on_save_button: string;
      };
    };
    details: {
      delete_button: string;
      fields: {
        password: string;
        application_name: string;
        username: string;
        language: string;
        project_identification: string;
        serial: string;
        profile: string;
        timezone: string;
        institution_id: string;
        instance_name: string;
        manufacturer: string;
        model: string;
        user_id: string;
        patient_id: string;
        email: string;
      };
      errors: {
        not_found_message: string;
      };
    };
  };
  caregivers: {
    root: {
      title: string;
      search_input_placeholder: string;
      name_header: string;
      email_header: string;
      state_header: string;
      state_value_true: string;
      state_value_false: string;
      no_caregivers: string;
    };
  };
  professional_caregivers: {
    root: {
      title: string;
      invite_button: string;
      search_input_placeholder: string;
      name_header: string;
      email_header: string;
      affiliation_type_header: string;
      state_header: string;
      state_value_true: string;
      state_value_false: string;
      no_professional_caregivers: string;
      deleted_professional_caregiver_dialog_title: string;
      deleted_professional_caregiver_dialog_message: string;
      deleted_professional_caregiver_dialog_ok_button: string;
      colaborator: string;
      colaborators: string;
      modals: {
        add_staff_flow: {
          form: {
            title: string;
            cancel: string;
            send_invite: string;
            create_profile: string;
            inputs: {
              name: {
                label: string;
                placeholder: string;
                required_message: string;
                pattern_message: string;
                max_message: string;
              };
              email: {
                label: string;
                placeholder: string;
                pattern_message: string;
                required_message: string;
              };
              roles: {
                label: string;
                placeholder: string;
              };
              invitation_type: {
                label: string;
                placeholder: string;
                required_message: string;
                via_email: string;
                via_sms: string;
                via_quickAccessCode: string;
              };
              phone_number: {
                label: string;
                placeholder: string;
                required_message: string;
                is_valid_message: string;
              };
              quick_access_code: {
                label: string;
                placeholder: string;
                required_message: string;
                is_valid_message: string;
              };
            };
          };
          confirm: {
            title: string;
            title_quickAccessCode: string;
            confirm_title: string;
            confirm_title_quickAccessCode: string;
            confirm_subtitle: string;
            cancel: string;
            send_invite: string;
            create_profile: string;
          };
          success: {
            title: string;
            title_quickAccessCode: string;
            confirm_title: string;
            confirm_title_quickAccessCode: string;
            confirm_subtitle: string;
            confirm_subtitle_quickAccessCode: string;
            confirm: string;
          };
          snackbar: {
            success_message: string;
            error_message: string;
            success_message_create_profile: string;
            error_message_create_profile: string;
          };
        };
      };
    };
  };
  caregiver_tabs: {
    tabs: {
      profile: string;
      affiliations: string;
      applications: string;
      user_cards: string;
      playlists: string;
    };
    errors: {
      caregiver_does_not_exist_error: string;
    };
  };
  user_affiliations: {
    entity_name_header: string;
    entity_type_header: string;
    affiliation_type_header: string;
    remove_affiliation_header: string;
    remove_affiliation_button: string;
    remove_affiliation_dialog_title: string;
    remove_affiliation_dialog_message: string;
    remove_affiliation_dialog_action_yes_button: string;
    remove_affiliation_dialog_action_no_button: string;
    update_affiliation_dialog_title: string;
    update_affiliation_dialog_message: string;
    update_affiliation_dialog_action_yes_button: string;
    update_affiliation_dialog_action_no_button: string;
    no_affiliations: string;
    errors: {
      remove_affiliation_error_message: string;
      update_affiliation_error_message: string;
    };
  };
  user_applications: {
    entity_filter: string;
    search_input_placeholder: string;
    name_header: string;
    category_header: string;
    state_header: string;
    state_value_true: string;
    state_value_false: string;
    disabled_state_tooltip: string;
    no_applications: string;
    enable_application_dialog_title: string;
    enable_application_dialog_message: string;
    enable_application_for_entity_dialog_message: string;
    enable_application_dialog_action_yes_button: string;
    enable_application_dialog_action_no_button: string;
    disable_application_dialog_title: string;
    disable_application_dialog_message: string;
    disable_application_for_entity_dialog_message: string;
    disable_application_dialog_action_yes_button: string;
    disable_application_dialog_action_no_button: string;
    no_affiliations_info_message: string;
    errors: {
      enable_application_error_message: string;
      disable_application_error_message: string;
      update_state_application_error_message: string;
    };
  };
  user_form: {
    title_create_senior: string;
    title_current_user_profile: string;
    delete_button: string;
    delete_caregiver_dialog_title: string;
    delete_caregiver_dialog_content: string;
    delete_senior_dialog_title: string;
    delete_senior_dialog_content: string;
    delete_caregiver_invite_title: string;
    delete_caregiver_invite_content: string;
    entity_input: string;
    full_name_input: string;
    display_name_input: string;
    state_input: string;
    state_value_true: string;
    state_value_false: string;
    gender_input: string;
    gender_placeholder_input: string;
    birthday_input: string;
    date_placeholder_input: string;
    admission_date_input: string;
    departure_date_input: string;
    address_input: string;
    address_placeholder_input: string;
    religion_input: string;
    religion_placeholder_input: string;
    profession_input: string;
    profession_description_input: string;
    social_network_city_input: string;
    language_input: string;
    language_placeholder_input: string;
    region_input: string;
    region_placeholder_input: string;
    input_disabled_by_unknown_region: string;
    email_input: string;
    email_placeholder_input: string;
    mobile_phone_number_input: string;
    mobile_phone_number_placeholder_input: string;
    mobile_and_landline_phone_number_input: string;
    tax_payer_number_input: string;
    tax_payer_number_input_placeholder: string;
    patient_number_input: string;
    patient_number_input_placeholder: string;
    social_security_number_input: string;
    social_security_number_input_placeholder: string;
    room_input: string;
    room_placeholder_input: string;
    social_responses_input: string;
    social_responses_placeholder_input: string;
    profile_picture_input: string;
    process_number_input: string;
    process_number_input_placeholder: string;
    recurring_fee_input: string;
    recurring_fee_input_placeholder: string;
    marital_status_input: string;
    marital_status_input_placeholder: string;
    aditional_info_input: string;
    additional_info_input_placeholder: string;
    education_level_input: string;
    education_level_input_placeholder: string;
    citizen_card_input: string;
    citizen_card_input_placeholder: string;
    cancel_button: string;
    save_button: string;
    saving_button: string;
    deleting_button: string;
    create_senior_dialog_title: string;
    create_senior_dialog_message: string;
    edit_senior_dialog_title: string;
    edit_senior_dialog_message: string;
    edit_caregiver_dialog_title: string;
    edit_caregiver_dialog_message: string;
    edit_caregiver_affiliation_type_dialog_title: string;
    edit_caregiver_affiliation_type_dialog_message: string;
    edit_current_user_profile_dialog_title: string;
    edit_current_user_profile_dialog_message: string;
    save_dialog_action_cancel_button: string;
    save_dialog_action_save_button: string;
    senior_router_prompt_edit_title: string;
    senior_router_prompt_create_title: string;
    senior_router_prompt_create_message: string;
    senior_router_prompt_edit_message: string;
    caregiver_edit_router_prompt_edit_title: string;
    caregiver_edit_router_prompt_edit_message: string;
    caregiver_edit_affiliation_type_router_prompt_edit_title: string;
    caregiver_edit_affiliation_type_router_prompt_edit_message: string;
    current_user_router_prompt_edit_title: string;
    current_user_router_prompt_edit_message: string;
    router_prompt_action_cancel_button: string;
    router_prompt_action_ok_button: string;
    senior_create_cancel_toast: string;
    senior_edit_cancel_toast: string;
    caregiver_edit_cancel_toast: string;
    current_user_edit_cancel_toast: string;
    caregiver_edit_affiliation_type_cancel_toast: string;
    current_user_edit_email_not_allowed_tooltip_title: string;
    caregiver_edit_email_not_allowed_tooltip_title: string;
    current_user_edit_state_not_allowed_tooltip_title: string;
    profile_picture_info_message: string;
    display_name_other_user_info_message: string;
    display_name_current_user_info_message: string;
    roles_ids_input: string;
    roles_ids_placeholder_input: string;
    input_placeholder: string;
    not_specified: string;
    input_read_only_tooltip: string;
    quick_access_code_input: string;
    quick_access_code_redefine_button: string;
    quick_access_code_disabled: string;
    success: {
      save_success_button: string;
      delete_success_button: string;
      create_senior_success_message: string;
      edit_senior_success_message: string;
      edit_caregiver_success_message: string;
      edit_caregiver_roles_ids_success_message: string;
      edit_current_user_success_message: string;
      edit_current_user_app_language_to_chosen_language_success_message: string;
      edit_current_user_app_language_to_browser_language_success_message: string;
      edit_current_user_app_language_to_default_language_success_message: string;
      success_delete_senior: string;
      success_delete_caregiver: string;
      success_delete_invite: string;
    };
    errors: {
      error_on_save_button: string;
      error_on_delete_senior_button: string;
      error_on_delete_caregiver_button: string;
      error_on_delete_invite: string;
      edit_form_validation_invalid_field: string;
      caregiver_edit_affiliation_type_form_validation_invalid_field: string;
      form_validation_invalid_phone_number: string;
      form_validation_not_mobile_phone_number: string;
      form_validation_invalid_email: string;
      form_validation_invalid_number: string;
      form_validation_invalid_room_name: string;
      form_validation_room_maximum_characters: string;
      form_validation_invalid_display_name_maximum_characters: string;
      create_active_senior_licenses_number_exceeded_error: string;
      activate_senior_licenses_number_exceeded_error: string;
      form_validation_invalid_quick_access_code: string;
      form_validation_process_number_max_chars: string;
      form_validation_recurring_fee_max_chars: string;
      form_validation_aditional_info_max_chars: string;
    };
  };
  user_attachments: {
    list: {
      headers: {
        name: string;
        description: string;
        author: string;
        date: string;
        size: string;
      };
      actions: {
        edit: string;
        delete: string;
        download: string;
      };
    };
    empty: {
      title: string;
      subtitle: string;
    };
    modals: {
      create: {
        title: string;
      };
      edit: {
        title: string;
        name: string;
        description: string;
        errors: {
          name_required: string;
          name_max_length: string;
          description_max_length: string;
        };
      };
      delete: {
        title: string;
        description: string;
      };
    };
    success: {
      create: string;
      edit: string;
      delete: string;
    };
    error: {
      create: string;
      edit: string;
      delete: string;
      download: string;
    };
    access_denied: {
      create: string;
      edit: string;
      delete: string;
    };
  };
  entity_services: {
    root: {
      title: string;
      add_button: string;
      search_input_placeholder: string;
      name_header: string;
      display_order_header: string;
      state_header: string;
      state_value_true: string;
      state_value_false: string;
      no_services: string;
      deleted_service_dialog_title: string;
      deleted_service_dialog_message1: string;
      deleted_service_dialog_message2: string;
      deleted_service_dialog_ok_button: string;
    };
    edit: {
      title_create: string;
      title_edit: string;
      title_view: string;
      exit_button: string;
      save_button: string;
      saving_button: string;
      title_input: string;
      state_input: string;
      phone_number_input: string;
      phone_number_use_general_number: string;
      phone_number_use_specific_number: string;
      display_order_input: string;
      description_input: string;
      details_input: string;
      attach_document_input: string;
      document_informative_note_input: string;
      icon_input: string;
      image_input: string;
      button_preview: string;
      state_value_true: string;
      state_value_false: string;
      number_input: string;
      upload: string;
      remove_document: string;
      view_document: string;
      image_info: string;
      delete_button: string;
      deleting_button: string;
      delete_dialog_title: string;
      delete_dialog_content: string;
      delete_dialog_action_yes_button: string;
      delete_dialog_action_no_button: string;
      router_prompt_edit_title: string;
      router_prompt_create_title: string;
      router_prompt_create_message: string;
      router_prompt_edit_message: string;
      router_prompt_action_cancel_button: string;
      router_prompt_action_ok_button: string;
      create_service_dialog_title: string;
      create_service_dialog_message: string;
      edit_service_dialog_title: string;
      edit_service_dialog_message: string;
      save_service_dialog_action_cancel_button: string;
      save_service_dialog_action_save_button: string;
      service_create_cancel_toast: string;
      service_edit_cancel_toast: string;
      errors: {
        error_on_save_button: string;
        error_on_delete_button: string;
        edit_form_validation_invalid_field: string;
        create_form_validation_invalid_field: string;
        create_form_validation_invalid_icon: string;
        create_form_validation_invalid_image: string;
        form_validation_invalid_phone_number: string;
        service_does_not_exist_error: string;
      };
      success: {
        save_success_button: string;
        delete_success_button: string;
        create_success_message: string;
        edit_success_message: string;
      };
    };
  };
  applications: {
    system_administrator: {
      root: {
        title: string;
        search_input_placeholder: string;
        name_header: string;
        category_header: string;
        state_header: string;
        state_value_true: string;
        state_value_false: string;
        no_applications: string;
        add_button: string;
      };
      edit: {
        entities: {
          search_input_placeholder: string;
          name_header: string;
          type_header: string;
          state_header: string;
          state_value_true: string;
          state_value_false: string;
          no_entities: string;
          enable_application_dialog_message: string;
          enable_application_dialog_action_yes_button: string;
          enable_application_dialog_action_no_button: string;
          disable_application_dialog_message: string;
          disable_application_dialog_action_yes_button: string;
          disable_application_dialog_action_no_button: string;
          errors: {
            enable_application_error_message: string;
            disable_application_error_message: string;
            update_state_application_error_message: string;
          };
        };
      };
    };
    professional_caregiver: {
      root: {
        search_input_placeholder: string;
        name_header: string;
        category_header: string;
        state_header: string;
        state_value_true: string;
        state_value_false: string;
        no_applications: string;
        enable_application_dialog_message: string;
        enable_application_dialog_action_yes_button: string;
        enable_application_dialog_action_no_button: string;
        disable_application_dialog_message: string;
        disable_application_dialog_action_yes_button: string;
        disable_application_dialog_action_no_button: string;
        errors: {
          enable_application_error_message: string;
          disable_application_error_message: string;
          update_state_application_error_message: string;
        };
      };
      edit: {
        users: {
          search_input_placeholder: string;
          name_header: string;
          type_header: string;
          state_header: string;
          state_value_true: string;
          state_value_false: string;
          no_users: string;
          enable_application_dialog_message: string;
          enable_application_dialog_action_yes_button: string;
          enable_application_dialog_action_no_button: string;
          disable_application_dialog_message: string;
          disable_application_dialog_action_yes_button: string;
          disable_application_dialog_action_no_button: string;
          errors: {
            enable_application_error_message: string;
            disable_application_error_message: string;
            update_state_application_error_message: string;
          };
        };
      };
    };
  };
  application_tabs: {
    tabs: {
      details: string;
      entities: string;
      users: string;
    };
    errors: {application_does_not_exist_error: string};
  };
  application_form: {
    name_input: string;
    state_input: string;
    state_value_true: string;
    state_value_false: string;
    description_input: string;
    is_system_application_input: string;
    is_system_application_value_yes: string;
    is_system_application_value_no: string;
    available_for_user_types_input: string;
    icon_input: string;
    picture_input: string;
    category_input: string;
    activity_type_input: string;
    windows_key_input: string;
    android_key_input: string;
    input_disabled_tooltip: string;
  };
  devices: {
    root: {
      search_input_placeholder: string;
      identifier_header: string;
      type_header: string;
      state_header: string;
      state_value_true: string;
      state_value_false: string;
      user_header: string;
      no_devices: string;
      add_button: string;
    };
  };
  device_tabs: {
    device_title: string;
    tabs: {
      details: string;
      health: string;
      information: string;
    };
    errors: {device_does_not_exist_error: string};
  };
  user_cards: {
    root: {
      title: string;
      search_input_placeholder: string;
      name_header: string;
      state_header: string;
      state_value_true: string;
      state_value_false: string;
      user_header: string;
      no_cards: string;
      add_button: string;
      deleted_card_dialog_title: string;
      deleted_card_dialog_message: string;
      deleted_card_dialog_ok_button: string;
    };
    edit: {errors: {card_does_not_exist_error: string}};
  };
  gdpr_erasure_requests: {
    root: {
      title: string;
      add_button: string;
      no_requests: string;
      status_header: string;
      requested_at_header: string;
      completed_at_header: string;
      error_header: string;
      user_id_hash_header: string;
      user_email_hash_header: string;
      status_processing: string;
      status_completed: string;
      status_failed: string;
    };
    create_modal: {
      title: string;
      email_label: string;
      email_placeholder: string;
      email_required: string;
      email_invalid: string;
      cancel_button: string;
      submit_button: string;
      submitting_button: string;
      confirm_title: string;
      confirm_description: string;
      confirm_cannot_be_undone: string;
      confirm_button: string;
      cancel_confirm_button: string;
    };
    errors: {
      forbidden: string;
      user_not_found: string;
      unknown: string;
    };
    success: {
      request_created: string;
    };
  };
  entity_photos: {
    root: {
      add_button: string;
      deleted_photo_dialog_title: string;
      deleted_photo_dialog_message: string;
      deleted_photo_dialog_ok_button: string;
      entity_photos_only_for_computer_info_message: string;
    };
    edit: {
      title_create: string;
      title_edit: string;
      photo_input: string;
      photos_input: string;
      description_input: string;
      exit_button: string;
      save_button: string;
      saving_button: string;
      photos_create_cancel_toast: string;
      photo_edit_cancel_toast: string;
      delete_button: string;
      deleting_button: string;
      delete_dialog_title: string;
      delete_dialog_content: string;
      delete_dialog_action_yes_button: string;
      delete_dialog_action_no_button: string;
      router_prompt_edit_title: string;
      router_prompt_edit_message: string;
      router_prompt_create_title: string;
      router_prompt_create_message: string;
      router_prompt_action_cancel_button: string;
      router_prompt_action_ok_button: string;
      photos_info: string;
      choose_photos_button: string;
      input_disabled_tooltip: string;
      errors: {
        error_on_save_button: string;
        error_on_delete_button: string;
        photo_does_not_exist_error: string;
        create_form_validation_invalid_field: string;
        form_validation_invalid_description_maximum_characters: string;
      };
      success: {
        save_success_button: string;
        delete_success_button: string;
        create_success_message: string;
        edit_success_message: string;
      };
      warnings: {
        partial_create_success_message: string;
      };
    };
  };
  entities: {
    root: {
      title: string;
      search_input_placeholder: string;
      name_header: string;
      type_header: string;
      state_header: string;
      state_value_true: string;
      state_value_false: string;
      no_entities: string;
      add_button: string;
      deleted_entity_dialog_title: string;
      deleted_entity_dialog_message: string;
      deleted_entity_dialog_ok_button: string;
    };
    edit: {
      tabs: {
        details: string;
        seniors: string;
        professional_caregivers: string;
        applications: string;
        devices: string;
        user_cards: string;
        activity: string;
        statistics: string;
        licenses: string;
        playlists: string;
      };
      seniors: {
        search_input_placeholder: string;
        name_header: string;
        gender_header: string;
        state_header: string;
        state_value_true: string;
        state_value_false: string;
        no_seniors: string;
        enable_senior_dialog_title: string;
        enable_senior_dialog_message: string;
        enable_senior_dialog_action_yes_button: string;
        enable_senior_dialog_action_no_button: string;
        disable_senior_dialog_title: string;
        disable_senior_dialog_message: string;
        disable_senior_dialog_action_yes_button: string;
        disable_senior_dialog_action_no_button: string;
        errors: {
          enable_senior_error_message: string;
          disable_senior_error_message: string;
          update_state_senior_error_message: string;
        };
      };
      professional_caregivers: {
        search_input_placeholder: string;
        invite_button: string;
        name_header: string;
        email_header: string;
        affiliation_type_header: string;
        state_header: string;
        state_value_true: string;
        state_value_false: string;
        no_professional_caregivers: string;
      };
      applications: {
        search_input_placeholder: string;
        name_header: string;
        category_header: string;
        state_header: string;
        state_value_true: string;
        state_value_false: string;
        no_applications: string;
        enable_application_dialog_message: string;
        enable_application_dialog_action_yes_button: string;
        enable_application_dialog_action_no_button: string;
        disable_application_dialog_message: string;
        disable_application_dialog_action_yes_button: string;
        disable_application_dialog_action_no_button: string;
        errors: {
          enable_application_error_message: string;
          disable_application_error_message: string;
          update_state_application_error_message: string;
        };
      };
      devices: {
        search_input_placeholder: string;
        identifier_header: string;
        type_header: string;
        state_header: string;
        state_value_true: string;
        state_value_false: string;
        user_header: string;
        no_devices: string;
      };
      user_cards: {
        search_input_placeholder: string;
        name_header: string;
        state_header: string;
        state_value_true: string;
        state_value_false: string;
        user_header: string;
        no_cards: string;
        front_card_design_button: string;
        back_card_design_button: string;
      };
      playlists: {
        search_input_placeholder: string;
        type_filter: string;
        name_header: string;
        state_header: string;
        state_value_true: string;
        state_value_false: string;
        no_playlist: string;
        enable_playlist_dialog_title: string;
        enable_playlist_dialog_message: string;
        enable_playlist_dialog_action_yes_button: string;
        enable_playlist_dialog_action_no_button: string;
        disable_playlist_dialog_title: string;
        disable_playlist_dialog_message: string;
        disable_playlist_dialog_action_yes_button: string;
        disable_playlist_dialog_action_no_button: string;
        errors: {
          enable_playlist_error_message: string;
          disable_playlist_error_message: string;
          update_state_playlist_error_message: string;
        };
      };
      playlist_items: {
        title: string;
        search_input_placeholder: string;
        title_header: string;
        state_header: string;
        state_value_true: string;
        state_value_false: string;
        no_items: string;
        enable_item_dialog_title: string;
        enable_item_dialog_message: string;
        enable_item_dialog_action_yes_button: string;
        enable_item_dialog_action_no_button: string;
        disable_item_dialog_title: string;
        disable_item_dialog_message: string;
        disable_item_dialog_action_yes_button: string;
        disable_item_dialog_action_no_button: string;
        errors: {
          enable_item_error_message: string;
          disable_item_error_message: string;
          update_state_item_error_message: string;
          playlist_does_not_exist_error: string;
        };
      };
      licenses: {
        number_of_monthly_active_licenses_not_defined: string;
        number_of_monthly_active_licenses_title: string;
        save_button: string;
        saving_button: string;
        edit_dialog_title: string;
        edit_dialog_content: string;
        edit_dialog_action_cancel_button: string;
        edit_dialog_action_save_button: string;
        router_prompt_title: string;
        router_prompt_message: string;
        router_prompt_action_cancel_button: string;
        router_prompt_action_ok_button: string;
        number_of_monthly_active_licenses_edit_cancel_toast: string;
        success: {save_success_button: string};
        errors: {
          error_on_save_button: string;
          edit_form_validation_invalid_field: string;
          form_validation_invalid_number_of_monthly_active_licenses: string;
        };
      };
      errors: {entity_does_not_exist_error: string};
    };
  };
  affiliated_entity_profile: {
    errors: {entity_does_not_exist_error: string};
  };
  section_tabs: {exit_button: string};
  profession_input: {no_options: string; loading: string};
  social_network_city_input: {no_options: string; loading: string};
  user_menu: {
    logout: string;
    profile: string;
    settings: string;
    office: string;
    dailyCareLog: string;
    errors: {logout_error_message: string};
  };
  error_box: {
    title: string;
    description: string;
    details_button: string;
  };
  country_select: {
    title: string;
    no_options: string;
  };
  tablet_button: {
    empty_title: string;
  };
  icons_picker: {
    title: string;
    cancel_button: string;
    confirm_button: string;
  };
  image_cropper_dialog: {
    title: string;
    zoom: string;
    rotation: string;
    save_button: string;
    cancel_button: string;
  };
  loading_content: {
    loading: string;
  };
  pdf_viewer: {
    error: string;
  };
  activity_card: {
    today: string;
    yesterday: string;
    this_week: string;
    this_month: string;
    previous_month: string;
    past: string;
    entity: string;
    device: string;
    duration: string;
    end_type: string;
    participants: string;
    application: string;
    application_battle_with: string;
    level_on_app: string;
    missed_call: string;
    rejected_call: string;
    call_started: string;
    drawing_sent: string;
    drawing_saved: string;
    drawing_shared: string;
    painting_sent: string;
    painting_saved: string;
    painting_shared: string;
    and: string;
    photo_sent: string;
    photo_saved: string;
    photo_shared: string;
    camera_used: string;
    response: string;
    video_message_sent_to: string;
    video_message_sent_by: string;
    video_sent: string;
    video_saved: string;
    video_shared: string;
    currently_happening: string;
  };
  notification_card: {
    open_invite: string;
    open_occurrences: string;
    relationship_invite: {
      message_sender: string;
      message_sender_same_as_target: string;
      message_sender_unknown: string;
    };
    affiliation_invite: {
      message_sender: string;
      message_sender_same_as_target: string;
      message_sender_unknown: string;
    };
    user_signed_in: string;
    photo_sent: string;
    photo_shared: string;
    drawing_sent: string;
    drawing_shared: string;
    painting_sent: string;
    painting_shared: string;
    video_sent: string;
    video_shared: string;
    without_license: string;
    missed_call: string;
    video_message: string;
    sos_request: string;
    reminder_response: {
      message: string;
      responses: {
        positive: string;
        negative: string;
        not_responding: string;
      };
    };
    relationship_invite_request_result: {
      message_accepted: string;
      message_declined: string;
    };
    caregiver_administrator_role_request_result: {
      message_accepted: string;
      message_declined: string;
    };
    relationship_role_change: {
      message_senior_myself: string;
      message_senior_other: string;
      message_caregiver_myself: string;
      message_caregiver_other: string;
      message_unknown_myself: string;
      message_unknown_other: string;
    };
    personal_data_sharing_preferences_change: {
      message: string;
      current_data_settings_title: string;
      can_access_profile_data: string;
      can_access_contacts: string;
      can_access_applications: string;
      can_access_multimedia: string;
      can_access_reminders: string;
      can_access_activity_feed: string;
    };
    relationship_end: {
      message_myself: string;
      message_other: string;
    };
    media_processing_status: {
      video_message: {
        [ProcessStatus.Done]: string;
        [ProcessStatus.Failed]: string;
      };
      video_shared: {
        [ProcessStatus.Done]: string;
        [ProcessStatus.Failed]: string;
      };
      video_saved: {
        [ProcessStatus.Done]: string;
        [ProcessStatus.Failed]: string;
      };
      photo_shared: {
        [ProcessStatus.Done]: string;
        [ProcessStatus.Failed]: string;
      };
      photo_saved: {
        [ProcessStatus.Done]: string;
        [ProcessStatus.Failed]: string;
      };
      unknown: {
        [ProcessStatus.Done]: string;
        [ProcessStatus.Failed]: string;
      };
    };
    media_empty: string;
    new_occurrence: string;
    new_occurrence_patient: string;
    new_occurrence_assigned: string;
    new_occurrence_assigned_patient: string;
  };
  section_list: {
    refresh: string;
    no_more_data: string;
    without_data: string;
    today: string;
    old_f: string;
    old_m: string;
  };
  activity_timeline: {title: string};
  clipboard_text_field: {
    copy_to_clipboard_button: string;
    copy_to_clipboard_tooltip_message: string;
    success: {copy_to_clipboard_success_message: string};
  };
  activity_end_types: {
    [ActivityEndType.UserAction]: string;
    [ActivityEndType.SystemTimeout]: string;
  };
  device_types: {
    [DeviceType.Handset]: string;
    [DeviceType.Tablet]: string;
    [DeviceType.Desktop]: string;
    [DeviceType.Laptop]: string;
    [DeviceType.Unknown]: string;
  };
  camera_facing: {
    [CameraFacing.Front]: string;
    [CameraFacing.Rear]: string;
    [CameraFacing.External]: string;
    [CameraFacing.Unknown]: string;
  };
  reminder_response_values: {
    [ReminderResponseValue.Positive]: string;
    [ReminderResponseValue.Negative]: string;
    [ReminderResponseValue.NotResponding]: string;
  };
  invite_states: {
    [InviteState.Accepted]: string;
    [InviteState.Cancelled]: string;
    [InviteState.Open]: string;
    [InviteState.Pending]: string;
    [InviteState.Refused]: string;
    [InviteState.RefusedBySenior]: string;
    [InviteState.WaitingForSeniorApproval]: string;
    [InviteState.AcceptedBySenior]: string;
    [InviteState.Expired]: string;
  };
  process_statuses: {
    [ProcessStatus.Done]: string;
    [ProcessStatus.InProgress]: string;
    [ProcessStatus.ToDo]: string;
    [ProcessStatus.Undefined]: string;
    [ProcessStatus.Failed]: string;
  };
  entity_types: {
    [EntityType.DayCareCenter]: string;
    [EntityType.Family]: string;
    [EntityType.Generic]: string;
    [EntityType.HomeSupport]: string;
    [EntityType.Institution]: string;
    [EntityType.InsuranceCompany]: string;
    [EntityType.TechCompany]: string;
  };
  care_taks_not_done_reasons: {
    [CareTaskNotDoneReason.SeniorRefusal]: string;
    [CareTaskNotDoneReason.seniorAbsence]: string;
    [CareTaskNotDoneReason.LackOfMaterial]: string;
    [CareTaskNotDoneReason.SeniorWasNotFeelingWell]: string;
    [CareTaskNotDoneReason.Other]: string;
  };
  time_units: {seconds: string; minutes: string; hours: string};
  entity_form: {
    exit_button: string;
    create_entity_title: string;
    picture_input: string;
    name_input: string;
    name_placeholder_input: string;
    state_input: string;
    state_value_true: string;
    state_value_false: string;
    description_input: string;
    type_input: string;
    language_input: string;
    language_placeholder_input: string;
    region_input: string;
    region_placeholder_input: string;
    time_zone_input: string;
    time_zone_placeholder_input: string;
    email_input: string;
    email_placeholder_input: string;
    short_name_input: string;
    short_name_placeholder_input: string;
    anniversary_input: string;
    anniversary_placeholder_input: string;
    capacity_input: string;
    capacity_placeholder_input: string;
    address_input: string;
    address_placeholder_input: string;
    website_input: string;
    website_placeholder_input: string;
    number_input: string;
    landline_phone_number_input: string;
    mobile_phone_number_input: string;
    phone_number_placeholder: string;
    services_name_input: string;
    services_name_placeholder_input: string;
    services_phone_number_input: string;
    services_image_url_input: string;
    services_image_info_message: string;
    picture_info_message: string;
    special_purpose_input: string;
    special_purpose_placeholder_input: string;
    social_responses_input: string;
    social_responses_placeholder_input: string;
    save_button: string;
    saving_button: string;
    deleting_button: string;
    delete_button: string;
    delete_dialog_title: string;
    delete_dialog_content: string;
    delete_dialog_action_yes_button: string;
    delete_dialog_action_no_button: string;
    router_prompt_create_title: string;
    router_prompt_create_message: string;
    router_prompt_edit_title: string;
    router_prompt_edit_message: string;
    router_prompt_action_cancel_button: string;
    router_prompt_action_ok_button: string;
    edit_cancel_toast: string;
    create_cancel_toast: string;
    edit_special_purpose_not_allowed_tooltip_title: string;
    edit_is_active_not_allowed_tooltip_title: string;
    input_disabled_tooltip: string;
    not_specified: string;
    alert: {
      short_name_input: string;
    };
    success: {
      save_success_button: string;
      delete_success_button: string;
      create_success_message: string;
      edit_success_message: string;
    };
    errors: {
      error_on_save_button: string;
      error_on_delete_button: string;
      edit_form_validation_invalid_field: string;
      create_form_validation_invalid_field: string;
      form_validation_invalid_full_name_maximum_characters: string;
      form_validation_invalid_landline_phone_number: string;
      form_validation_invalid_mobile_phone_number: string;
      form_validation_not_mobile_phone_number: string;
      form_validation_invalid_services_phone_number: string;
      form_validation_invalid_email: string;
      form_validation_invalid_website: string;
      form_validation_invalid_capacity: string;
      form_validation_required_social_responses: string;
    };
  };
  professional_caregiver_invite_form: {
    exit_button: string;
    title: string;
    entity_input: string;
    name_input: string;
    contact_method_input: string;
    email_input: string;
    mobile_phone_number_input: string;
    affiliation_type_input: string;
    save_dialog_title: string;
    save_dialog_message: string;
    save_dialog_action_cancel_button: string;
    save_dialog_action_save_button: string;
    router_prompt_title: string;
    router_prompt_message: string;
    router_prompt_action_cancel_button: string;
    router_prompt_action_ok_button: string;
    cancel_toast: string;
    save_button: string;
    saving_button: string;
    success: {save_success_button: string; success_message: string};
    errors: {
      error_on_save_button: string;
      form_validation_invalid_field: string;
      form_validation_invalid_mobile_phone_number: string;
      form_validation_not_mobile_phone_number: string;
      form_validation_invalid_email: string;
    };
  };
  invite_contact_methods: {
    [InviteContactMethod.Email]: string;
    [InviteContactMethod.MobilePhoneNumber]: string;
  };
  search_select_input: {
    no_options: string;
    loading: string;
    more_button: string;
    items_showing_information: string;
  };
  user_card_form: {
    exit_button: string;
    create_user_card_title: string;
    edit_user_card_title: string;
    view_user_card_title: string;
    name_input: string;
    state_input: string;
    state_value_true: string;
    state_value_false: string;
    tag_input: string;
    entity_input: string;
    user_input: string;
    user_info_message: string;
    save_button: string;
    saving_button: string;
    deleting_button: string;
    delete_button: string;
    delete_dialog_title: string;
    delete_dialog_content: string;
    create_dialog_title: string;
    create_dialog_message: string;
    edit_dialog_title: string;
    edit_dialog_message: string;
    router_prompt_create_title: string;
    router_prompt_create_message: string;
    router_prompt_edit_title: string;
    router_prompt_edit_message: string;
    router_prompt_action_cancel_button: string;
    router_prompt_action_ok_button: string;
    edit_cancel_toast: string;
    create_cancel_toast: string;
    disabled_tooltip: string;
    success: {
      save_success_button: string;
      delete_success_button: string;
      create_success_message: string;
      edit_success_message: string;
      senior_inactive_success: string;
    };
    errors: {
      error_on_save_button: string;
      error_on_delete_button: string;
      edit_form_validation_invalid_field: string;
      create_form_validation_invalid_field: string;
      error_on_delete_user: string;
      error_on_marking_senior_as_inactive: string;
    };
  };
  device_form: {
    exit_button: string;
    save_button: string;
    saving_button: string;
    create_device_title: string;
    type_input: string;
    state_input: string;
    state_value_true: string;
    state_value_false: string;
    number_input: string;
    entity_input: string;
    user_input: string;
    language_input: string;
    language_placeholder_input: string;
    sign_in_type_input: string;
    sign_in_type_placeholder_input: string;
    mac_address_input: string;
    serial_number_input: string;
    user_info_message: string;
    edit_type_not_allowed_tooltip_title: string;
    edit_serial_number_not_allowed_tooltip_title: string;
    edit_mac_address_not_allowed_tooltip_title: string;
    edit_number_not_allowed_tooltip_title: string;
    edit_sign_in_type_not_allowed_tooltip_title: string;
    input_disabled_tooltip: string;
    deleting_button: string;
    delete_button: string;
    delete_dialog_title: string;
    delete_dialog_content: string;
    create_dialog_title: string;
    create_dialog_message: string;
    edit_dialog_title: string;
    edit_dialog_message: string;
    save_dialog_action_cancel_button: string;
    save_dialog_action_save_button: string;
    router_prompt_create_title: string;
    router_prompt_create_message: string;
    router_prompt_edit_title: string;
    router_prompt_edit_message: string;
    router_prompt_action_cancel_button: string;
    router_prompt_action_ok_button: string;
    edit_cancel_toast: string;
    create_cancel_toast: string;
    success: {
      create_message: string;
      edit_message: string;
      deleted_message: string;
    };
    errors: {
      create_message: string;
      edit_message: string;
      edit_form_validation_invalid_field: string;
      create_form_validation_invalid_field: string;
      deleted_message: string;
    };
    isInDebugMode_input: string;
    isInDebugMode_placeholder_input: string;
  };
  user_sign_in_types: {
    [UserSignInType.Card]: string;
    [UserSignInType.Quick]: string;
    [UserSignInType.Code]: string;
  };
  entity_special_purposes: {
    [EntitySpecialPurpose.Commercial]: string;
    [EntitySpecialPurpose.Demonstration]: string;
    [EntitySpecialPurpose.Research]: string;
    [EntitySpecialPurpose.Testing]: string;
  };
  entity_statistics_dashboard: {
    title: string;
    refresh_button: string;
    text_charts: {
      number_of_active_seniors_title: string;
      number_of_active_seniors_subtitle: string;
      number_of_active_professional_caregivers_title: string;
      number_of_active_professional_caregivers_subtitle: string;
      number_of_active_informal_caregivers_title: string;
      number_of_active_informal_caregivers_subtitle: string;
      number_of_pending_invites_title: string;
      number_of_pending_invites_subtitle: string;
      number_of_calls_title: string;
      number_of_calls_subtitle: string;
      number_of_outgoing_calls_title: string;
      number_of_outgoing_calls_subtitle: string;
      number_of_incoming_calls_title: string;
      number_of_incoming_calls_subtitle: string;
    };
    table_charts: {
      number_of_active_devices_title: string;
      number_of_active_digital_inclusion_devices_title: string;
      number_of_active_quick_access_devices_title: string;
      number_of_active_devices_device_type_column: string;
      number_of_active_devices_total_column: string;
      top_users_title: string;
      top_users_subtitle: string;
      top_users_user_column: string;
      top_users_total_usage_time_column: string;
      top_users_usage_time_column_unit_label: string;
    };
    area_charts: {
      general_system_usage_title: string;
      general_system_usage_subtitle: string;
      general_system_usage_x_label: string;
      general_system_usage_y_label: string;
      general_system_usage_y_unit_label: string;
    };
    bar_charts: {
      calls_evolution_title: string;
      calls_evolution_subtitle: string;
      calls_evolution_x_label: string;
      calls_evolution_y_label: string;
    };
    pie_charts: {
      most_used_applications_title: string;
      most_used_applications_subtitle: string;
      most_used_applications_x_label: string;
      most_used_applications_y_label: string;
      most_used_applications_y_unit_label: string;
    };
    stack_charts: {
      applications_usage_title: string;
      applications_usage_subtitle: string;
      applications_usage_singular_stack_label: string;
      applications_usage_plural_stack_label: string;
      applications_usage_x_label: string;
      applications_usage_y_label: string;
      applications_usage_y_unit_label: string;
    };
  };
  senior_statistics_dashboard: {
    title: string;
    refresh_button: string;
    statistics_per_application_title: string;
    statistics_per_application_application_label: string;
    statistics_per_application_entity_label: string;
    statistics_per_application_entity_placeholder: string;
    statistics_per_application_info_message: string;
    text_charts: {
      number_of_informal_caregivers_title: string;
      number_of_informal_caregivers_subtitle: string;
      number_of_pending_invites_title: string;
      number_of_pending_invites_subtitle: string;
      number_of_calls_title: string;
      number_of_calls_subtitle: string;
      number_of_outgoing_calls_title: string;
      number_of_outgoing_calls_subtitle: string;
      number_of_incoming_calls_title: string;
      number_of_incoming_calls_subtitle: string;
      average_daily_usage_last_title: string;
      average_daily_usage_subtitle: string;
      average_daily_usage_unit_label: string;
      number_of_media_received_title: string;
      number_of_media_received_subtitle: string;
      number_of_video_messages_received_title: string;
      number_of_video_messages_sent_title: string;
      number_of_photos_sent_title: string;
      number_of_drawings_sent_title: string;
      number_of_paintings_sent_title: string;
      last_access_date_time_title: string;
    };
    table_charts: {
      top_preferences_title: string;
      top_preferences_subtitle: string;
      top_preferences_application_column: string;
      top_preferences_total_usage_time_column: string;
      top_preferences_total_usage_time_column_unit_label: string;
    };
    area_charts: {
      general_system_usage_title: string;
      general_system_usage_subtitle: string;
      general_system_usage_x_label: string;
      general_system_usage_y_label: string;
      general_system_usage_y_unit_label: string;
      application_usage_title: string;
      application_usage_subtitle: string;
      application_usage_x_label: string;
      application_usage_y_label: string;
      application_usage_y_unit_label: string;
    };
    pie_charts: {
      most_used_applications_title: string;
      most_used_applications_subtitle: string;
      most_used_applications_x_label: string;
      most_used_applications_y_label: string;
      most_used_applications_y_unit_label: string;
    };
    stack_charts: {
      applications_usage_title: string;
      applications_usage_subtitle: string;
      applications_usage_singular_stack_label: string;
      applications_usage_plural_stack_label: string;
      applications_usage_x_label: string;
      applications_usage_y_label: string;
      applications_usage_y_unit_label: string;
    };
    line_charts: {
      application_evolution_title: string;
      application_evolution_subtitle: string;
      application_evolution_x_label: string;
      application_evolution_y_label: string;
    };
  };
  charts: {
    no_data: string;
  };
  media_gallery: {
    refresh: string;
    no_more_data: string;
    without_data: string;
  };
  application_category_form: {
    exit_button: string;
    create_application_category_title: string;
    save_button: string;
    saving_button: string;
    default_name_input: string;
    default_description_input: string;
    enabled_state_color_input: string;
    pressed_state_color_input: string;
    name_translations_input: string;
    description_translations_input: string;
    deleting_button: string;
    delete_button: string;
    delete_dialog_title: string;
    delete_dialog_content: string;
    delete_dialog_action_yes_button: string;
    delete_dialog_action_no_button: string;
    router_prompt_create_title: string;
    router_prompt_create_message: string;
    router_prompt_edit_title: string;
    router_prompt_edit_message: string;
    router_prompt_action_cancel_button: string;
    router_prompt_action_ok_button: string;
    edit_cancel_toast: string;
    create_cancel_toast: string;
    create_dialog_title: string;
    create_dialog_message: string;
    edit_dialog_title: string;
    edit_dialog_message: string;
    save_dialog_action_cancel_button: string;
    save_dialog_action_save_button: string;
    success: {
      save_success_button: string;
      delete_success_button: string;
      create_success_message: string;
      edit_success_message: string;
    };
    errors: {
      error_on_save_button: string;
      error_on_delete_button: string;
      edit_form_validation_invalid_field: string;
      create_form_validation_invalid_field: string;
      form_validation_invalid_color: string;
    };
  };
  playlist_types: {
    [PlaylistType.Karaoke]: string;
    [PlaylistType.Movies]: string;
    [PlaylistType.Music]: string;
  };
  playlists: {
    system_administrator: {
      root: {
        title: string;
        search_input_placeholder: string;
        type_filter: string;
        name_header: string;
        no_playlists: string;
        add_button: string;
        deleted_playlist_dialog_title: string;
        deleted_playlist_dialog_message: string;
        deleted_playlist_dialog_ok_button: string;
      };
      edit: {
        tabs: {
          details: string;
          items: string;
          entities: string;
        };
        items: {
          search_input_placeholder: string;
          title_header: string;
          no_items: string;
          add_button: string;
          deleted_playlist_item_dialog_title: string;
          deleted_playlist_item_dialog_message: string;
          deleted_playlist_item_dialog_ok_button: string;
        };
        entities: {
          search_input_placeholder: string;
          name_header: string;
          type_header: string;
          state_header: string;
          state_value_true: string;
          state_value_false: string;
          no_entities: string;
          enable_playlist_dialog_title: string;
          enable_playlist_dialog_message: string;
          enable_playlist_dialog_action_yes_button: string;
          enable_playlist_dialog_action_no_button: string;
          disable_playlist_dialog_title: string;
          disable_playlist_dialog_message: string;
          disable_playlist_dialog_action_yes_button: string;
          disable_playlist_dialog_action_no_button: string;
          errors: {
            enable_playlist_error_message: string;
            disable_playlist_error_message: string;
            update_state_playlist_error_message: string;
          };
        };
        errors: {playlist_does_not_exist_error: string};
      };
      playlist_item: {
        tabs: {
          details: string;
          entities: string;
        };
        entities: {
          search_input_placeholder: string;
          name_header: string;
          type_header: string;
          state_header: string;
          state_value_true: string;
          state_value_false: string;
          no_entities: string;
          enable_item_dialog_title: string;
          enable_item_dialog_message: string;
          enable_item_dialog_action_yes_button: string;
          enable_item_dialog_action_no_button: string;
          disable_item_dialog_title: string;
          disable_item_dialog_message: string;
          disable_item_dialog_action_yes_button: string;
          disable_item_dialog_action_no_button: string;
          errors: {
            enable_item_error_message: string;
            disable_item_error_message: string;
            update_state_item_error_message: string;
          };
        };
        errors: {item_does_not_exist_error: string};
      };
    };
    professional_caregiver: {
      root: {
        search_input_placeholder: string;
        type_filter: string;
        name_header: string;
        state_header: string;
        state_value_true: string;
        state_value_false: string;
        no_playlists: string;
        enable_playlist_dialog_title: string;
        enable_playlist_dialog_message: string;
        enable_playlist_dialog_action_yes_button: string;
        enable_playlist_dialog_action_no_button: string;
        disable_playlist_dialog_title: string;
        disable_playlist_dialog_message: string;
        disable_playlist_dialog_action_yes_button: string;
        disable_playlist_dialog_action_no_button: string;
        errors: {
          enable_playlist_error_message: string;
          disable_playlist_error_message: string;
          update_state_playlist_error_message: string;
        };
      };
      edit: {
        tabs: {
          items: string;
          users: string;
          details: string;
        };
        items: {
          search_input_placeholder: string;
          title_header: string;
          state_header: string;
          state_value_true: string;
          state_value_false: string;
          no_items: string;
          enable_item_dialog_title: string;
          enable_item_dialog_message: string;
          enable_item_dialog_action_yes_button: string;
          enable_item_dialog_action_no_button: string;
          disable_item_dialog_title: string;
          disable_item_dialog_message: string;
          disable_item_dialog_action_yes_button: string;
          disable_item_dialog_action_no_button: string;
          errors: {
            enable_item_error_message: string;
            disable_item_error_message: string;
            update_state_item_error_message: string;
          };
        };
        users: {
          search_input_placeholder: string;
          name_header: string;
          type_header: string;
          state_header: string;
          state_value_true: string;
          state_value_false: string;
          no_users: string;
          enable_playlist_dialog_title: string;
          enable_playlist_dialog_message: string;
          enable_playlist_dialog_action_yes_button: string;
          enable_playlist_dialog_action_no_button: string;
          disable_playlist_dialog_title: string;
          disable_playlist_dialog_message: string;
          disable_playlist_dialog_action_yes_button: string;
          disable_playlist_dialog_action_no_button: string;
          errors: {
            enable_playlist_error_message: string;
            disable_playlist_error_message: string;
            update_state_playlist_error_message: string;
          };
        };
        errors: {playlist_does_not_exist_error: string};
      };
      playlist_item: {
        tabs: {
          users: string;
          details: string;
        };
        users: {
          search_input_placeholder: string;
          name_header: string;
          type_header: string;
          state_header: string;
          state_value_true: string;
          state_value_false: string;
          no_users: string;
          enable_item_dialog_title: string;
          enable_item_dialog_message: string;
          enable_item_dialog_action_yes_button: string;
          enable_item_dialog_action_no_button: string;
          disable_item_dialog_title: string;
          disable_item_dialog_message: string;
          disable_item_dialog_action_yes_button: string;
          disable_item_dialog_action_no_button: string;
          errors: {
            enable_item_error_message: string;
            disable_item_error_message: string;
            update_state_item_error_message: string;
          };
        };
        errors: {item_does_not_exist_error: string};
      };
    };
  };
  user_playlists: {
    search_input_placeholder: string;
    type_filter: string;
    entity_filter: string;
    name_header: string;
    state_header: string;
    state_value_true: string;
    state_value_false: string;
    state_disabled_tooltip: string;
    no_playlists: string;
    no_affiliations_info_message: string;
    enable_playlist_dialog_title: string;
    enable_playlist_dialog_message: string;
    enable_playlist_for_entity_dialog_message: string;
    enable_playlist_dialog_action_yes_button: string;
    enable_playlist_dialog_action_no_button: string;
    disable_playlist_dialog_title: string;
    disable_playlist_dialog_message: string;
    disable_playlist_for_entity_dialog_message: string;
    disable_playlist_dialog_action_yes_button: string;
    disable_playlist_dialog_action_no_button: string;
    errors: {
      enable_playlist_error_message: string;
      disable_playlist_error_message: string;
      update_state_playlist_error_message: string;
    };
  };
  time_lapse_types: {
    [TimeLapseType.Custom]: string;
    [TimeLapseType.Last180Days]: string;
    [TimeLapseType.Last30Days]: string;
    [TimeLapseType.Last365Days]: string;
    [TimeLapseType.LastMonth]: string;
    [TimeLapseType.LastWeek]: string;
    [TimeLapseType.ThisMonth]: string;
    [TimeLapseType.ThisWeek]: string;
  };
  time_lapse: {
    dialog_title: string;
    range_label: string;
    start_text: string;
    end_text: string;
    to_text: string;
    cancel_button: string;
    confirm_button: string;
  };
  device_health: {
    no_data: string;
    last_recording: string;
    basic_information_section_title: string;
    wifi_section_title: string;
    networks_wifi_subsection_title: string;
    current_wifi_subsection_title: string;
    mobile_data_section_title: string;
    graphics_section_title: string;
    controllers_graphics_subsection_title: string;
    displays_graphics_subsection_title: string;
    system_section_title: string;
    battery_section_title: string;
    disks_section_title: string;
    operating_system_section_title: string;
    cpu_section_title: string;
    memory_section_title: string;
    applications_section_title: string;
    versions_section_title: string;
    request_tracking_section_title: string;
    cache_images_section_title: string;
    creation_date_label: string;
    expiration_date_label: string;
    entity_label: string;
    user_label: string;
    data_usage_label: string;
    ssid_label: string;
    quality_label: string;
    ip_address_label: string;
    is_data_usage_enabled_label: string;
    carrier_label: string;
    network_type_label: string;
    dialer_label: string;
    signal_strength_label: string;
    signal_strength_level_label: string;
    signal_strength_no: string;
    signal_strength_weak: string;
    signal_strength_fair: string;
    signal_strength_good: string;
    signal_strength_strong: string;
    brand_label: string;
    model_label: string;
    is_main_display_label: string;
    width_label: string;
    height_label: string;
    current_width_label: string;
    current_height_label: string;
    current_scale_factor_label: string;
    sku_label: string;
    serial_number_label: string;
    version_label: string;
    has_battery_label: string;
    is_battery_charging_label: string;
    is_battery_connected_to_power_label: string;
    battery_level_label: string;
    size_label: string;
    used_disks_label: string;
    available_label: string;
    mount_label: string;
    name_label: string;
    distribution_label: string;
    release_label: string;
    build_label: string;
    web_view_version_label: string;
    cores_label: string;
    used_memory_label: string;
    country_label: string;
    region_label: string;
    city_label: string;
    city_longitude_label: string;
    city_latitude_label: string;
    language_label: string;
    user_ip_address_label: string;
    yes_label: string;
    no_label: string;
    gb_unit_label: string;
    mb_unit_label: string;
    px_unit_label: string;
    agent_label: string;
    chrome_label: string;
    electron_label: string;
    launcher_binary_label: string;
    launcher_frontend_label: string;
  };
  operative_system_names: {
    [OperativeSystemName.Android]: string;
    [OperativeSystemName.Linux]: string;
    [OperativeSystemName.MacOS]: string;
    [OperativeSystemName.Other]: string;
    [OperativeSystemName.Windows]: string;
    [OperativeSystemName.iOS]: string;
  };
  mobile_network_types: {
    [MobileNetworkType.Unspecified]: string;
    [MobileNetworkType._1G]: string;
    [MobileNetworkType._2G]: string;
    [MobileNetworkType._3G]: string;
    [MobileNetworkType._4G]: string;
    [MobileNetworkType._5G]: string;
  };
  home: {
    refreshing: string;
    release_to_refresh: string;
    pull_to_refresh: string;
    none: {
      root: {
        no_associations_message_title: string;
        no_associations_message_subtitle: string;
      };
    };
  };
  localized_string_input: {
    cancel_button: string;
    confirm_button: string;
    has_zero_translations_tooltip_message: string;
    has_one_translation_tooltip_message: string;
    has_many_translations_tooltip_message: string;
  };
  user_playlist_items: {
    title: string;
    search_input_placeholder: string;
    title_header: string;
    state_header: string;
    state_value_true: string;
    state_value_false: string;
    state_disabled_tooltip: string;
    no_items: string;
    enable_item_dialog_title: string;
    enable_item_dialog_message: string;
    enable_item_dialog_action_yes_button: string;
    enable_item_dialog_action_no_button: string;
    disable_item_dialog_title: string;
    disable_item_dialog_message: string;
    disable_item_dialog_action_yes_button: string;
    disable_item_dialog_action_no_button: string;
    errors: {
      enable_item_error_message: string;
      disable_item_error_message: string;
      update_state_item_error_message: string;
      playlist_does_not_exist_error: string;
    };
  };
  playlist_form: {
    exit_button: string;
    create_playlist_title: string;
    save_button: string;
    saving_button: string;
    default_name_input: string;
    name_translations_input: string;
    default_description_input: string;
    description_translations_input: string;
    enabled_state_color_input: string;
    pressed_state_color_input: string;
    state_input: string;
    state_value_true: string;
    state_value_false: string;
    type_input: string;
    active_by_default_for_all_entities_input: string;
    active_by_default_for_all_entities_placeholder_input: string;
    yes_value: string;
    no_value: string;
    image_url_input: string;
    image_info_message: string;
    regional_lockout_input: string;
    regional_lockout_placeholder: string;
    deleting_button: string;
    delete_button: string;
    delete_dialog_title: string;
    delete_dialog_content: string;
    delete_dialog_action_yes_button: string;
    delete_dialog_action_no_button: string;
    save_dialog_action_cancel_button: string;
    save_dialog_action_save_button: string;
    router_prompt_create_title: string;
    router_prompt_create_message: string;
    router_prompt_edit_title: string;
    router_prompt_edit_message: string;
    router_prompt_action_cancel_button: string;
    router_prompt_action_ok_button: string;
    edit_cancel_toast: string;
    create_cancel_toast: string;
    success: {
      save_success_button: string;
      delete_success_button: string;
      create_success_message: string;
      edit_success_message: string;
    };
    errors: {
      error_on_save_button: string;
      error_on_delete_button: string;
      edit_form_validation_invalid_field: string;
      create_form_validation_invalid_field: string;
      form_validation_invalid_color: string;
    };
  };
  playlist_item_form: {
    exit_button: string;
    create_playlist_item_title: string;
    save_button: string;
    saving_button: string;
    deleting_button: string;
    delete_button: string;
    delete_dialog_title: string;
    delete_dialog_content: string;
    delete_dialog_action_yes_button: string;
    delete_dialog_action_no_button: string;
    create_dialog_title: string;
    create_dialog_message: string;
    save_dialog_action_cancel_button: string;
    save_dialog_action_save_button: string;
    router_prompt_create_title: string;
    router_prompt_create_message: string;
    router_prompt_edit_title: string;
    router_prompt_edit_message: string;
    router_prompt_action_cancel_button: string;
    router_prompt_action_ok_button: string;
    edit_cancel_toast: string;
    create_cancel_toast: string;
    default_title_input: string;
    title_translations_input: string;
    default_description_input: string;
    description_translations_input: string;
    regional_lockout_input: string;
    regional_lockout_placeholder: string;
    state_input: string;
    state_value_true: string;
    state_value_false: string;
    playlist_input: string;
    resource_url_input: string;
    resource_input: string;
    thumbnail_image_url_input: string;
    thumbnail_image_info_message: string;
    success: {
      save_success_button: string;
      delete_success_button: string;
      create_success_message: string;
      edit_success_message: string;
    };
    errors: {
      error_on_save_button: string;
      error_on_delete_button: string;
      edit_form_validation_invalid_field: string;
      create_form_validation_invalid_field: string;
      form_validation_invalid_resource_url: string;
    };
  };
  licenses: {
    professional_caregiver: {
      root: {
        refresh_button: string;
        number_of_monthly_active_licenses_title: string;
        number_of_active_seniors_title: string;
        number_of_monthly_active_licenses_not_defined: string;
      };
    };
  };
  videocall: {
    you: string;
    incomingCall: string;
    incomingCallConnecting: string;
    incomingCallConnectingHint: string;
    answer: string;
    decline: string;
    settingsTitle: string;
    settingsMicrophone: string;
    settingsCamera: string;
    settingsSpeaker: string;
    default: string;
    callFailed: {
      title: string;
      ok: string;
      e486: string;
      e487: string;
      e603: string;
      e404: string;
      e480: string;
      e402: string;
    };
  };
  entityFrontCardDesignTool: {
    title: string;
    step1: string;
    step2: string;
    step3: string;
    entityPicture: string;
    chooseAnotherEntityPicture: string;
    entityName: string;
    addEntityName: string;
    entityNameToBePrinted: string;
    entityNameTextColor: string;
    entityNameTextFont: string;
    next: string;
    save: string;
    restart: string;
    failedToLoadUsers: string;
  };
  entityBackCardDesignTool: {
    title: string;
    step1: string;
    step2: string;
    entityName: string;
    entityNumber: string;
    seniorUsersCount: string;
    caregiverUsersCount: string;
    cardName: string;
    entityPictureHeight: string;
    lineColor: string;
    includeEntityName: string;
    generatePreview: string;
    saveCards: string;
    saveSpreadsheet: string;
    restart: string;
  };
  cards: {
    pendingInvitation: string;
    years: string;
  };
  dashboard_side_menu: {
    dashboard: string;
    general_activity: string;
    statistics: string;
    general_statistics: string;
    social_statistics: string;
  };
  digital_inclusion_side_menu: {
    applications: string;
    details: string;
    users: string;
    photos: string;
    cards: string;
    devices: string;
    services: string;
    playlists: string;
  };
  seniorSideMenu: {
    generalStatistics: string;
    healthStatistics: string;
    basicActivitiesDailyLife: string;
    careAndActivity: string;
    individualPlan: string;
    personalData: string;
    careServices: string;
  };
  teamSideMenu: {
    professionals: string;
    shifts: {
      title: string;
      shiftsManagement: string;
      shiftsAllocations: string;
    };
    attendanceTracker: {
      title: string;
      assignments: string;
      registry: string;
    };
    careServices: string;
    permissions: string;
    profileDropdown: {
      profile: string;
      careServices: string;
      applications: string;
      cards: string;
      playLists: string;
    };
  };
  profileSideMenu: {
    profile: string;
  };
  settingsSideMenu: {
    institution: string;
    activities_sectors: {
      load_error: string;
    };
  };
  responsibleUserDropdown: {
    noResponsibleDefined: string;
    noCoordinatorDefined: string;
  };
  activities_group_entry: {
    modals: {
      edit: {
        title: string;
        observations_max_characters: string;
      };
      confirm_delete: {
        title: string;
        copy: {
          title: string;
          subtitle: string;
        };
      };
    };
    activity_types: string;
    no_activity_types: string;
    configuration_type_not_defined: string;
    configuration_type_label: string;
    exceptions: string;
    edit_details: string;
    add_activities: string;
    configure_first: string;
    actions: {
      exit: string;
      edit: string;
      delete: string;
      history: string;
    };
    snackbar: {
      create: {
        success: string;
        error: string;
      };
      update: {
        success: string;
        error: string;
      };
      delete: {
        success: string;
        error: string;
      };
      configuration: {
        success: string;
        error: string;
      };
    };
    error: string;
  };
  activities_type_entry: {
    modals: {
      edit: {
        title: string;
      };
      delete: {
        title: string;
        copy: {
          title: string;
          subtitle: string;
        };
      };
      view: {
        leave: string;
        view_only: string;
      };
    };
    snackbar: {
      update: {
        success: string;
        error: string;
      };
      delete: {
        success: string;
        error: string;
      };
      configuration: {
        success: string;
        error: string;
      };
    };
    templates: {
      name: string;
      description: string;
      activities_save: string;
      activities_import: string;
      check_all: string;
      snackbar: {
        success_message: string;
        error_message: string;
        import_success_message: string;
        import_error_message: string;
      };
      template: string;
      confirmation_title: string;
      confirmation_subtitle: string;
      confirmation_replace_title: string;
      confirmation_replace_subtitle: string;
      confirm: string;
      name_error: string;
      description_error: string;
      select: string;
    };
  };
  dynamic_form: {
    required_message: string;
    pattern_message: {
      invalid: string;
      max_characters: string;
    };
    error_loading: string;
    feature_fields: {
      autocomplete: {
        load_more: string;
        loading: string;
        no_options: string;
        placeholder: string;
        counter: string;
      };
    };
  };
  modal: {
    ok: string;
    cancel: string;
    confirm: string;
    add: string;
    save: string;
    remove: string;
    import: string;
    back: string;
    next: string;
    delete: string;
    revert: string;
    archive: string;
  };
  daily_care_log: {
    generics: {
      back: string;
    };
    senior_actions: {
      open_actions: string;
      title: string;
    };
    tabs: {
      home: string;
      occurrences: string;
      attendance_tracker: string;
    };
    date_carousel: {
      yesterday: string;
      today: string;
      tomorrow: string;
    };
    task_table: {
      tasks: string;
      empty: string;
      select_all: string;
      sort: string;
      drawer: {
        clear: string;
        save: string;
        filters: {
          title: string;
          social_responses: string;
        };
        sort: {
          title: string;
          name: string;
          autonomy: string;
          status: string;
          ascending: string;
          descending: string;
        };
      };
    };
    cards: {
      entity_user: {
        task_conclusion_summary_label: {
          concluded: string;
          task: string;
        };
      };
    };
    home: {
      my_shifts: string;
      care_services: string;
      other_shifts: string;
      no_allocated_shifts: string;
      no_other_shifts: string;
      no_care_services: string;
      previous_days_shift: string;
    };
    seniors: {
      title: string;
      no_seniors: string;
    };
    task_details: {
      task_not_done_reason: string;
      task_done: string;
      task_not_done: string;
      add_a_note: string;
      cancel: string;
      save: string;
      register: string;
      edit: string;
      edit_entry: string;
      disabled_tooltip: string;
    };
    shift_collapsible: {
      shift: string;
      other_shift_members: string;
      no_other_shift_members: string;
    };
    shift_details: {
      all_tasks_handled: string;
      tabs: {
        simple: {title: string};
        grouped: {title: string; task: string};
        user: {
          title: string;
          grid: {
            empty: string;
            empty_search_message: {
              title: string;
              subtitle: string;
            };
          };
        };
      };
      unplanned_tasks: {
        header_title: string;
        activity_sector: string;
        activity: string;
        name: string;
        execution_date: string;
        execution_hour: string;
        add_success_toast: string;
        add_error_toast: string;
        upload_error_toast: string;
        add_multiple_success_toast: string;
        add_cancel_modal_title: string;
        add_cancel_modal_subtitle: string;
        add_user_placeholder: string;
        add_files: string;
        delete_attachment: string;
        files_size: string;
        file_size_error: string;
        file_not_supported_error: string;
        file_both_errors: string;
      };
    };
    grouped_tasks_details: {
      select_bottom_bar: {
        count: string;
        count_plural: string;
        next: string;
      };
    };
    grouped_tasks_handling: {
      snackbar: {
        loading_tasks: {
          error: string;
        };
        updating_tasks: {
          success: string;
          error: string;
        };
      };
    };
    attendance_tracker: {
      title: string;
      empty_title: string;
      empty_subtitle: string;
      register: string;
      filters: {
        sections: {
          attendanceMovements: string;
          attendanceStatus: string;
          attendancePeriods: string;
        };
        values: {
          voided: string;
          entry: string;
          exit: string;
          open: string;
          closed: string;
          morning: string;
          afternoon: string;
          night: string;
        } & Record<string, string>;
      };
      session: {
        entry: string;
        exit: string;
        running: string;
        overdue: string;
        closed: string;
        elapsed: string;
        overdue_alert: string;
        total: string;
      };
      bar: {
        running: string;
        since: string;
        not_started: string;
      };
      manual: {
        registered_by: string;
        entry_registered_by: string;
        exit_registered_by: string;
        notice: string;
        reason: string;
        no_location: string;
        unknown_user: string;
      };
      voided: {
        tag: string;
        notice: string;
        reason: string;
      };
      register_entry: string;
      register_exit: string;
      load_error: string;
      empty_other_day: string;
      details: {
        no_exit: string;
        open_in_maps: string;
        time_zone: string;
      };
      new: {
        title_entry: string;
        title_exit: string;
        save_entry: string;
        save_exit: string;
        record_type: string;
        entry: string;
        exit: string;
        professional: string;
        entity: string;
        date_time: string;
        location: string;
        latitude: string;
        longitude: string;
        accuracy: string;
        accuracy_value: string;
        waiting: string;
        accuracy_info: string;
        unavailable: string;
        loading_error: string;
        retry: string;
        success_entry: string;
        success_exit: string;
        type_conflict: string;
        save_error: string;
        purpose: string;
        permission: {
          prompt_title: string;
          prompt_description: string;
          allow: string;
          denied_title: string;
          denied_description: string;
          unsupported_title: string;
          unsupported_description: string;
          help: {
            chrome: string;
            edge: string;
            firefox: string;
            safari: string;
            ios: string;
            android_chrome: string;
            generic: string;
          };
        };
      };
    };
    occurrences: {
      empty_title: string;
      empty_filters_title: string;
      empty_subtitle: string;
      new: string;
      new_occurrence: string;
      add: string;
      type: string;
      date: string;
      time: string;
      local: string;
      responsible: string;
      patient: string;
      details: string;
      add_success_toast: string;
      add_error_toast: string;
      add_cancel_modal_title: string;
      add_cancel_modal_subtitle: string;
      local_error: string;
    };
  };
  care_and_activities: {
    date: string;
    activity: string;
    task: string;
    evaluation: string;
    responsible: string;
    status: string;
    empty_state: string;
    permissions: string;
    register: string;
    notes: string;
    status_types: {
      [CaregiverTaskCompletion.DONE]: string;
      [CaregiverTaskCompletion.NOT_DONE]: string;
      [CaregiverTaskCompletion.PLANNED]: string;
      [CaregiverTaskCompletion.MISSED]: string;
    };
    sort_icon: {
      sort_in_ascending: string;
      sort_in_descending: string;
    };
    handle_task_modal: {
      task_status: {
        done: string;
        not_done: string;
      };
      details: string;
      observations: string;
      treated: string;
      cancel: string;
      write_observation: string;
      observation_required_message: string;
      card_collapse: {
        title: string;
      };
      time_input: string;
      date_input: string;
      select_date: string;
      select_time: string;
      task_info_load_error: string;
      feature_input: {
        label: {
          [CareTaskFeature.BloodGlucoseHealthMeasurement]: string;
          [CareTaskFeature.BloodOxygenHealthMeasurement]: string;
          [CareTaskFeature.BloodPressureHealthMeasurement]: string;
          [CareTaskFeature.BodyFatPercentageHealthMeasurement]: string;
          [CareTaskFeature.BodyHeightHealthMeasurement]: string;
          [CareTaskFeature.BodyMusclePercentageHealthMeasurement]: string;
          [CareTaskFeature.BodyTemperatureHealthMeasurement]: string;
          [CareTaskFeature.BodyWaterPercentageHealthMeasurement]: string;
          [CareTaskFeature.BodyWeightHealthMeasurement]: string;
          [CareTaskFeature.HeartRateHealthMeasurement]: string;
          [CareTaskFeature.NatriureticPeptideTestHealthMeasurement]: string;
          [CareTaskFeature.RespiratoryRateHealthMeasurement]: string;
          [CareTaskFeature.SleepHealthMeasurement]: string;
          [CareTaskFeature.StepsHealthMeasurement]: string;
        };
        inputs: {
          value: {
            label: string;
            placeholder: string;
          };
          minutes: {
            label: string;
            placeholder: string;
          };
          percentage: {
            label: string;
            placeholder: string;
          };
          mealTime: {
            label: string;
            placeholder: string;
            options: {
              'before-meal': string;
              'after-meal': string;
            };
          };
          bnp: {
            label: string;
            placeholder: string;
          };
          nt_proBnp: {
            label: string;
            placeholder: string;
          };
          type: {
            label: string;
            placeholder: string;
            options: {
              'in-bed': string;
              awake: string;
              rem: string;
              core: string;
              deep: string;
            };
          };
          startTime: {
            label: string;
          };
          endTime: {
            label: string;
          };
        };
        units: {
          steps: string;
        };
        required_message: string;
        number_pattern_validation: {
          min: string;
          max: string;
        };
      };
      undo_task: string;
      undo: string;
      edit_entry: string;
      confirm: string;
      snackbar: {
        update: {
          success: string;
          error: string;
        };
        reset: {
          success: string;
          error: string;
        };
      };
      history: {
        title: string;
        update: string;
        status: string;
        someone: string;
        remove: string;
        auto: string;
        validate: string;
        create: string;
        read: string;
        contract_update: string;
        changes: string;
        task_not_done_reason: string;
        notes: string;
        files_added: string;
        files_removed: string;
      };
      files: string;
    };
  };
  configurations_fields: {
    feature: {
      name: {
        placeholder: string;
      };
    };
  };
  schedule_modal: {
    trigger_button_label: string;
    activity: {
      title: string;
    };
    task: {
      title: string;
    };
  };
  schedule_field: {
    readable_periodicity: {
      not_defined: string;
      no_repetition: string;
      defined_per_activity: string;
      at_every: string;
      hourly: string;
      daily: string;
      weekly: string;
      monthly: string;
      weekly_at: string;
      and: string;
      at_hour: string;
      until_end_day: string;
    };
    quantities: string;
  };
  activity_type_posology_schedule_form: {
    title: string;
    fields: {
      psology: {
        label: string;
      };
      posology_schedule_type: {
        label: string;
        options: {
          [PosologySchedule.Repetition]: string;
          [PosologySchedule.PeriodsOfTheDay]: string;
          [PosologySchedule.TimesList]: string;
          [PosologySchedule.SOS]: string;
        };
      };
      frequency: {
        options: {
          [Frequency.Hourly]: string;
          [Frequency.Daily]: string;
          [Frequency.Weekly]: string;
          [Frequency.Monthly]: string;
        };
      };
      frequency_value: {
        label: string;
        validations: {
          min: string;
        };
      };
      time_list: {
        hour: {
          label: string;
        };
        quantity: {
          label: string;
        };
        add: string;
        required: string;
        error: string;
      };
      periods_of_the_day_item: {
        required: string;
      };
      repetitionHourlyFrequencyValue: string;
      repetitionHourlyFrequencyValueHour: string;
      repetitionHourlyFrequencyValueHours: string;
      repetition_quantity: {
        label: string;
        validations: {
          min: string;
        };
      };
      end_date: {
        label: string;
        validations: {
          min: string;
        };
      };
      end_hour: {
        label: string;
        validations: {
          min: string;
        };
      };
    };
  };
  care_plan_sheet_activities_group_entry_configurationvalue: {
    repetition: {
      per_activity: string;
      per_task: string;
    };
    aivd: {
      at_time: string;
      transportation: string;
    };
  };
  periodicity_form: {
    empty_state: string;
    fields: {
      frequency: {
        label: string;
        hourly: string;
        daily: string;
        weekly: string;
        monthly: string;
      };
      frequency_value: {
        label: string;
        activity: string;
        task: string;
        validations: {
          required: string;
          min: string;
          max: string;
        };
      };
      start_at_date: {
        label: string;
        validations: {
          superior_to_end_date: string;
        };
      };
      end_date: {
        label: string;
        validations: {
          superior_to_start_date: string;
        };
      };
      end_hour: {
        label: string;
      };
      days: {
        label: string;
        validations: {
          required: string;
        };
      };
    };
  };
  role_permissions_professional_caregiver_office_screen: {
    generic_header: {
      subtitle: string;
    };
    fields: {
      name: {
        label: string;
        placeholder: string;
      };
      description: {
        label: string;
        placeholder: string;
      };
    };
    view: {
      tooltip: string;
      label: string;
    };
    edit: {
      tooltip: string;
      label: string;
    };
    select_all_permissions: string;
    localized_string_input: {
      name: {
        title: string;
      };
      description: {
        title: string;
      };
    };
    router_prompt: {
      title: string;
      content: {
        title: string;
        subtitle: string;
      };
    };
    snackbar: {
      load: {
        error: string;
      };
      update: {
        success: string;
        error: string;
      };
      exit: string;
    };
  };
  shifts_page: {
    shifts: string;
    team: string;
    create_button: string;
    schedule: string;
    to_hour: string;
    responsible: {
      label: string;
      not_defined: string;
    };
    professionals: {
      singular: string;
      plural: string;
    };
    teams: {
      singular: string;
      plural: string;
    };
    empty_state: {
      title: string;
      subtitle: string;
    };
    shift_modal: {
      create_shift: string;
      edit_shift: string;
      shift_form: {
        name: string;
        name_placeholder: string;
        required_field_message: string;
        min_required_field_message: string;
        pattern_field_message: string;
        max_characters_name_message: string;
        abbreviation: string;
        abbreviation_placeholder: string;
        color: string;
        social_response: string;
        social_responses_placeholder_input: string;
        people_per_shift: string;
        people_per_shift_placeholder_input: string;
        start_hour: string;
        end_hour: string;
        shift_responsible: string;
        shift_responsible_placeholder: string;
        snackbar: {
          create_shift: {
            success: string;
            error: string;
          };
          update_shift: {
            success: string;
            error: string;
          };
        };
      };
    };
    confirm_delete: {
      title: string;
      copy: {
        title: string;
        subtitle: string;
      };
    };
    cancel: string;
    create: string;
    save: string;
    delete: string;
  };
  planner: {
    header: {
      title: string;
      filter_all_seniors_label: string;
      filter_input_placeholder: string;
      range: {
        day: string;
        week: string;
        month: string;
      };
      add: string;
      add_disabled: string;
      toggler: {
        list: string;
        calendar: string;
      };
    };
    body: {
      untreaten: string;
      untreaten_one: string;
      untreaten_other: string;
      empty: string;
    };
    side: {
      see_all: string;
      birthdays: string;
    };
    modal: {
      create: {
        activity: {
          // step 1
          ativities_group: string;
          ativities_group_placeholder: string;
          activities_type: string;
          activities_type_placeholder: string;
          // step 3
          add_users_label: string;
          add_users_placeholder: string;
          added_users: string;
          add_users_type: {
            social_response: string;
            user: string;
          };
          add_users_mark: string;
          add_users_mark_one: string;
          add_users_mark_other: string;
          add_users_mark_all: string;
          // done
          success: string;
          success_one: string;
          success_other: string;
        };
      };
      delete: {
        title: {
          single: string;
          multiple: string;
        };
        desc: {
          single: string;
          multiple: string;
        };
      };
    };
    item_menu: {
      activity: {
        action: {
          edit: string;
          delete: string;
          delete_disabled: string;
          register: string;
          plan: string;
        };
        users: string;
      };
      anniversary: {
        birthday: string;
        born: string;
        years: string;
      };
    };
    errors: {
      content: string;
      save_activity: string;
      delete_activity: string;
      unknown_item_type: string;
    };
  };
  care_services: {
    header: {
      add_button_disabled_tooltip: string;
      seniors_label: string;
      filter_caregivers_label: string;
    };
    form: {
      required_field_message: string;
      title_label: string;
      title_placeholder: string;
      color_label: string;
      start_date_label: string;
      end_date_label: string;
      start_time_label: string;
      end_time_label: string;
      repetition_label: string;
      responsible_users_label: string;
      responsible_users_placeholder: string;
      senior_label: string;
      senior_placeholder: string;
      notes_label: string;
      notes_placeholder: string;
      permissions_disabled_tooltip: string;
      twenty_four_hours_label: string;
    };
    modals: {
      edit: {
        title: string;
      };
      create: {
        title: string;
      };
      delete: {
        title: string;
        description: string;
        cannot_be_undone: string;
      };
      repetition_options: {
        only_this_event: string;
        this_and_following: string;
      };
      edit_recurrent: {
        title: string;
      };
      delete_recurrent: {
        title: string;
      };
      edit_confirmation: {
        title: string;
        subtitle: string;
      };
    };
    toasts: {
      edit: {
        success: {
          title: string;
          message: string;
        };
        error: {
          unknown: {
            title: string;
            message: string;
          };
          not_found: {
            title: string;
            message: string;
          };
          access_denied: {
            title: string;
            message: string;
          };
          invalid_data: {
            title: string;
            message: string;
          };
          end_date_invalid: {
            title: string;
            message: string;
          };
          repetition_required: {
            title: string;
            message: string;
          };
          repetition_removal_scope: {
            title: string;
            message: string;
          };
          repetition_change_scope: {
            title: string;
            message: string;
          };
        };
      };
      create: {
        success: {
          title: string;
          message: string;
        };
        error: {
          title: string;
          message: string;
        };
      };
      delete: {
        error: {
          title: string;
          message: string;
        };
      };
    };
    export: {
      repetition: {
        ends_on: string;
        ends_after: string;
        occurrences: string;
      };
    };
    permissions: {
      view: string;
    };
  };
  shared: {
    handle_care_task_modal: {
      confirm_edit: {
        title: string;
      };
      confirm_edit_cancel: {
        title: string;
        subtitle: string;
      };
      confirm_reset: {
        title: string;
        subtitle: string;
      };
    };
    input_disabled_tooltip: string;
  };
  export: {
    plan: string;
    address: string;
    contributor_number: string;
    sns_number: string;
    contact: string;
    others: string;
    details: string;
    tasks: string;
    task: string;
    register: string;
    date: string;
    start_date: string;
    end_date: string;
    periodicity: string;
    observations: string;
    no_records: string;
    no_repetition: string;
    senior_profile: {
      save_pdf: string;
      report_title: string;
      user_section_title: string;
      profile_section_subtitle: string;
      full_name: string;
      preferred_name: string;
      birth_date: string;
      sns_number: string;
      social_security_number: string;
      taxpayer_number: string;
      additional_information: string;
      health_conditions_subtitle: string;
      food_intolerances_title: string;
      food_intolerances_subtitle: string;
      medication_allergies_subtitle: string;
      medication_title: string;
      latest_vital_signs_title: string;
    };
    error: {
      pdf_generation: string;
      xlsx_generation: string;
      pdf_monthly_not_supported: string;
    };
  };
}
